import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowClockwise,
  CheckCircle,
  Eye,
  PaperPlaneTilt,
  PlayCircle,
  SealCheck,
  WarningCircle,
  XCircle,
  Archive,
  CalendarPlus,
  Copy,
  Pause,
  Plus,
  PencilSimple,
} from "@phosphor-icons/react";
import { renderMarkdown } from "../lib/markdown";
import { TOOLS, getToolByKey } from "../lib/tools";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const adminEmails = () =>
  (process.env.REACT_APP_ADMIN_EMAILS || "kirovd555@gmail.com")
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

const isPlatformAdminUser = (user) =>
  !!user?.email && adminEmails().includes(String(user.email).toLowerCase());

const APPROVAL_TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const TASK_TABS = [
  { key: "ready", label: "Ready" },
  { key: "scheduled", label: "Scheduled" },
  { key: "published", label: "Published" },
  { key: "completed", label: "Completed" },
  { key: "failed", label: "Failed" },
  { key: "archived", label: "Archived" },
  { key: "all", label: "All" },
];

const WORKFLOW_TABS = [
  { key: "active", label: "Active" },
  { key: "paused", label: "Paused" },
  { key: "all", label: "All" },
];

const DAY_OPTIONS = [
  { key: "MO", label: "Monday" },
  { key: "TU", label: "Tuesday" },
  { key: "WE", label: "Wednesday" },
  { key: "TH", label: "Thursday" },
  { key: "FR", label: "Friday" },
  { key: "SA", label: "Saturday" },
  { key: "SU", label: "Sunday" },
];

function normalizePlan(plan) {
  return String(plan || "starter").trim().toLowerCase();
}

function planUnlocksWorkflows(plan) {
  return ["pro", "company", "agency", "enterprise"].includes(
    normalizePlan(plan)
  );
}

function getApiErrorMessage(err, fallback = "Action failed.") {
  const data = err?.response?.data;

  if (!data) return fallback;

  if (data.details && typeof data.details === "object") {
    const detailedMessages = Object.values(data.details)
      .flat()
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean);

    if (detailedMessages.length > 0) {
      return detailedMessages.join(" ");
    }
  }

  if (typeof data.error === "string") return data.error;
  if (typeof data.detail === "string") return data.detail;
  if (typeof data.message === "string") return data.message;
  if (typeof data.detail?.message === "string") return data.detail.message;

  return fallback;
}

function readItems(data) {
  if (Array.isArray(data)) return data;

  const possibleKeys = [
    "items",
    "workflows",
    "runs",
    "approvals",
    "tasks",
    "data",
    "results",
  ];

  for (const key of possibleKeys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
}

function safeJsonObject(value) {
  if (!value) return {};

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function normalizeStatus(status) {
  return String(status || "unknown").trim().toLowerCase();
}

function statusClass(status) {
  const normalized = normalizeStatus(status);

  if (["active", "approved", "published", "completed", "ok", "success"].includes(normalized)) {
    return "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]";
  }

  if (["pending", "ready", "scheduled", "running", "queued"].includes(normalized)) {
    return "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]";
  }

  if (["paused", "rejected", "failed", "error", "archived"].includes(normalized)) {
    return "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]";
  }

  return "bg-[#F5F5F5] text-[#525252] border-[#E5E7EB]";
}

function StatusPill({ status }) {
  return (
    <span
      className={`inline-flex items-center border px-2 py-1 text-[10px] font-mono uppercase tracking-[0.16em] ${statusClass(
        status
      )}`}
    >
      {status || "unknown"}
    </span>
  );
}

function EmptyState({ title, text, to, action }) {
  return (
    <div className="border border-dashed border-[#E5E7EB] bg-[#F5F5F5] p-8 sm:p-10 text-center">
      <SealCheck size={28} weight="duotone" className="mx-auto text-[#002FA7]" />

      <p className="font-display text-xl font-bold mt-4">{title}</p>

      <p className="text-sm text-[#525252] mt-2 max-w-md mx-auto">{text}</p>

      {to && (
        <Link to={to} className="btn-primary mt-6 inline-flex">
          {action || "Open Studio"}
        </Link>
      )}
    </div>
  );
}

function formatDate(value) {
  if (!value) return "Not recorded";

  const raw = String(value);
  const date = raw.includes("T") ? new Date(raw) : new Date(`${raw}Z`);

  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString();
}

function utcToLocalInput(value) {
  if (!value) return "";

  const date = String(value).includes("T") ? new Date(value) : new Date(`${value}Z`);
  if (Number.isNaN(date.getTime())) return "";

  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
}

function localInputToUtcMysql(value) {
  const localDate = new Date(value);
  if (Number.isNaN(localDate.getTime())) return "";

  return `${localDate.getUTCFullYear()}-${String(localDate.getUTCMonth() + 1).padStart(
    2,
    "0"
  )}-${String(localDate.getUTCDate()).padStart(2, "0")} ${String(
    localDate.getUTCHours()
  ).padStart(2, "0")}:${String(localDate.getUTCMinutes()).padStart(2, "0")}:00`;
}

function workflowId(workflow) {
  return workflow?.workflow_id || workflow?.id || workflow?._id;
}

function workflowPayload(workflow) {
  const payload =
    workflow?.input_payload ||
    workflow?.inputPayload ||
    workflow?.inputs ||
    workflow?.prompt_inputs ||
    {};

  return safeJsonObject(payload);
}

function workflowMeta(workflow) {
  const payload = workflowPayload(workflow);
  return safeJsonObject(workflow?._workflow || payload._workflow || payload.workflow);
}

function workflowSchedule(workflow) {
  const payload = workflowPayload(workflow);
  return safeJsonObject(workflow?.schedule || payload._schedule || payload.schedule);
}

function projectStatus(project) {
  return normalizeStatus(project?.status || "active");
}

function isArchivedProject(project) {
  return ["archived", "deleted"].includes(projectStatus(project));
}

function itemProjectId(item) {
  const payload = workflowPayload(item);
  const metadata = safeJsonObject(item?.metadata);
  const meta = workflowMeta(item);
  const project = safeJsonObject(
    item?.project ||
      payload?._project ||
      payload?.project ||
      metadata?.project ||
      meta?.project
  );

  return String(
    item?.project_id ||
      item?.projectId ||
      item?.project_uuid ||
      payload?.project_id ||
      payload?.projectId ||
      payload?._project?.project_id ||
      metadata?.project_id ||
      metadata?.projectId ||
      meta?.project_id ||
      project?.project_id ||
      project?.id ||
      ""
  ).trim();
}

function itemProjectName(item) {
  const payload = workflowPayload(item);
  const metadata = safeJsonObject(item?.metadata);
  const meta = workflowMeta(item);
  const project = safeJsonObject(
    item?.project ||
      payload?._project ||
      payload?.project ||
      metadata?.project ||
      meta?.project
  );

  return String(
    item?.project_name ||
      item?.projectName ||
      payload?.project_name ||
      payload?._project?.name ||
      metadata?.project_name ||
      meta?.project_name ||
      project?.name ||
      ""
  ).trim();
}

function itemProjectStatus(item) {
  const payload = workflowPayload(item);
  const metadata = safeJsonObject(item?.metadata);
  const meta = workflowMeta(item);
  const project = safeJsonObject(
    item?.project ||
      payload?._project ||
      payload?.project ||
      metadata?.project ||
      meta?.project
  );

  const rawStatus =
    item?.project_status ||
    payload?.project_status ||
    payload?._project?.status ||
    metadata?.project_status ||
    meta?.project_status ||
    project?.status ||
    "";

  const value = String(rawStatus || "").trim();
  return value === "" ? "" : normalizeStatus(value);
}

function isGenericProjectLabel(value) {
  const text = String(value || "").trim().toLowerCase();
  return text === "" || text === "project" || text === "projects" || text === "unknown project";
}

function projectDisplayName(project, fallback = "Project") {
  const directName = cleanDisplayTitle(project?.name || project?.project_name || "", "");

  if (directName && !isGenericProjectLabel(directName)) {
    return directName;
  }

  const fallbackText = cleanDisplayTitle(fallback, "Project");
  return fallbackText || "Project";
}

function cleanWorkflowInputsFromPayload(payload) {
  const source = payload && typeof payload === "object" && !Array.isArray(payload) ? payload : {};
  const cleaned = {};

  const internalKeys = new Set([
    "_workflow",
    "_schedule",
    "workflow",
    "schedule",
    "workflow_name",
    "workflow_title",
    "name",
    "title",
    "label",
    "display_name",
    "tool",
    "tool_key",
    "toolKey",
    "mode_type",
    "frequency",
    "cadence",
    "schedule_type",
    "run_time",
    "time",
    "day",
    "day_of_week",
    "status",
    "active",
    "is_active",
    "is_enabled",
    "approval_required",
    "require_approval",
    "create_action_task",
    "action_task_enabled",
    "inputs",
    "prompt_inputs",
    "input_payload",
  ]);

  const mergeInputs = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return;

    Object.entries(value).forEach(([key, innerValue]) => {
      if (internalKeys.has(key)) return;
      cleaned[key] = innerValue;
    });
  };

  // New backend shape: { product: "...", audience: "...", _workflow: {...} }
  mergeInputs(source);

  // Older frontend/backend test shapes saved the real fields inside one of these wrappers.
  // Without this, View / edit shows the placeholder examples instead of the saved inputs.
  mergeInputs(source.inputs);
  mergeInputs(source.prompt_inputs);
  mergeInputs(source.input_payload);

  return cleaned;
}

function defaultWorkflowNameForTool(toolKey) {
  const tool = getToolByKey(toolKey);
  return tool?.name ? `${tool.name} workflow` : "Workflow";
}

function workflowName(workflow) {
  const payload = workflowPayload(workflow);
  const meta = workflowMeta(workflow);
  const toolKey = workflowToolKey(workflow);

  const candidates = [
    workflow?.workflow_name,
    workflow?.name,
    workflow?.title,
    workflow?.workflow_title,
    workflow?.label,
    workflow?.display_name,
    meta?.name,
    meta?.workflow_name,
    payload?.workflow_name,
    payload?.workflow_title,
    payload?.name,
    payload?.title,
  ];

  const found = candidates.find((value) => String(value || "").trim() !== "");
  return found ? String(found).trim() : defaultWorkflowNameForTool(toolKey);
}

function cleanDisplayTitle(value, fallback = "Untitled") {
  const title = String(value || fallback)
    .replace(/�+/g, " - ")
    .replace(/\?\?+/g, " - ")
    .replace(/\s+[·•–—-]\s+/g, " - ")
    .replace(/\s+-\s+-\s+/g, " - ")
    .replace(/\s{2,}/g, " ")
    .trim();

  return title || fallback;
}

function workflowToolKey(workflow) {
  return String(
    workflow?.tool_key ||
      workflow?.tool ||
      workflow?.toolKey ||
      workflow?.tool_name ||
      "unknown"
  )
    .trim()
    .toLowerCase();
}

function workflowStatus(workflow) {
  const explicitStatus = normalizeStatus(workflow?.status || "");
  if (["paused", "inactive", "disabled"].includes(explicitStatus)) return "paused";
  if (["active", "enabled"].includes(explicitStatus)) return "active";

  if (
    workflow?.active === false ||
    workflow?.is_active === false ||
    workflow?.is_enabled === false ||
    workflow?.is_enabled === 0 ||
    workflow?.is_enabled === "0"
  ) {
    return "paused";
  }

  return "active";
}

function workflowScheduleText(workflow) {
  const schedule = workflowSchedule(workflow);
  const frequency =
    workflow?.frequency ||
    workflow?.schedule_type ||
    workflow?.cadence ||
    schedule?.frequency ||
    "scheduled";
  const time = workflow?.run_time || workflow?.time || schedule?.run_time || schedule?.time || "";
  const day = workflow?.day_of_week || workflow?.day || schedule?.day_of_week || schedule?.day || "";

  const normalizedFrequency = normalizeStatus(frequency);

  if (normalizedFrequency === "weekly" && day) {
    const found = DAY_OPTIONS.find(
      (item) =>
        normalizeStatus(item.key) === normalizeStatus(day) ||
        normalizeStatus(item.label) === normalizeStatus(day)
    );
    return `Weekly${found ? ` · ${found.label}` : ""}${time ? ` · ${time}` : ""}`;
  }

  if (normalizedFrequency === "daily") {
    return `Daily${time ? ` · ${time}` : ""}`;
  }

  if (normalizedFrequency === "monthly") {
    return `Monthly${time ? ` · ${time}` : ""}`;
  }

  return String(frequency);
}

function cleanInputObject(values) {
  const cleaned = {};

  Object.entries(values || {}).forEach(([key, value]) => {
    // Platform is intentionally hidden inside Workflow Center.
    // The actual publishing destination is chosen later from LinkedIn / WordPress / Newsletter actions.
    if (String(key || "").trim().toLowerCase() === "platform") {
      return;
    }

    const text = String(value || "").trim();
    if (text !== "") cleaned[key] = text;
  });

  return cleaned;
}

function workflowToForm(workflow) {
  const toolKey = workflowToolKey(workflow);
  const schedule = workflowSchedule(workflow);
  const payload = workflowPayload(workflow);
  const frequency =
    workflow?.frequency || workflow?.schedule_type || workflow?.cadence || schedule?.frequency || "weekly";

  return {
    name: workflowName(workflow),
    project_id: itemProjectId(workflow),
    project_name: itemProjectName(workflow),
    tool_key: toolKey === "unknown" ? "social" : toolKey,
    frequency: normalizeStatus(frequency) === "manual" ? "weekly" : normalizeStatus(frequency),
    day_of_week: workflow?.day_of_week || workflow?.day || schedule?.day_of_week || schedule?.day || "MO",
    run_time: workflow?.run_time || workflow?.time || schedule?.run_time || schedule?.time || "09:00",
    approval_required: Boolean(
      workflow?.approval_required === true ||
        workflow?.approval_required === 1 ||
        workflow?.approval_required === "1" ||
        workflow?.require_approval === true
    ),
    action_task_enabled: workflow?.create_action_task !== false && workflow?.action_task_enabled !== false,
    inputs: cleanWorkflowInputsFromPayload(payload),
  };
}

function buildWorkflowPayload(form, enabled = true) {
  const inputs = cleanInputObject(form.inputs || {});
  const frequency = normalizeStatus(form.frequency || "weekly");
  const workflowNameValue = String(form.name || "").trim() || defaultWorkflowNameForTool(form.tool_key);
  const projectIdValue = String(form.project_id || "").trim();
  const projectNameValue = String(form.project_name || "").trim();

  // Important: the PHP WorkflowController stores only input_payload as JSON.
  // So workflow name + schedule must also live inside input_payload, otherwise
  // the next page load cannot pre-fill View/Edit and the UI falls back to defaults.
  const storedInputPayload = {
    ...inputs,
    _workflow: {
      name: workflowNameValue,
    },
    _project: {
      project_id: projectIdValue,
      name: projectNameValue,
      status: "active",
    },
    project_id: projectIdValue,
    project_name: projectNameValue,
    _schedule: {
      frequency,
      day_of_week: form.day_of_week || "MO",
      run_time: form.run_time || "09:00",
      approval_required: Boolean(form.approval_required),
      action_task_enabled: Boolean(form.action_task_enabled),
    },
    workflow_name: workflowNameValue,
  };

  return {
    workflow_name: workflowNameValue,
    name: workflowNameValue,
    title: workflowNameValue,
    workflow_title: workflowNameValue,
    project_id: projectIdValue,
    projectId: projectIdValue,
    project_name: projectNameValue,
    tool_key: form.tool_key,
    tool: form.tool_key,
    mode_type: frequency === "manual" ? "saved" : "background",
    frequency,
    cadence: frequency,
    schedule_type: frequency,
    run_time: form.run_time,
    time: form.run_time,
    day_of_week: form.day_of_week,
    day: form.day_of_week,
    status: enabled ? "active" : "paused",
    active: enabled,
    is_active: enabled,
    is_enabled: enabled ? 1 : 0,
    approval_required: form.approval_required ? 1 : 0,
    require_approval: Boolean(form.approval_required),
    create_action_task: form.action_task_enabled ? 1 : 0,
    action_task_enabled: Boolean(form.action_task_enabled),
    inputs,
    prompt_inputs: inputs,
    input_payload: storedInputPayload,
  };
}

function copyText(text, label = "Copied.") {
  if (!text) return;
  navigator.clipboard.writeText(String(text));
  toast.success(label);
}

function generatedPreviewSaveType(item) {
  const value = String(
    item?.action_task_destination ||
      item?.save_destination ||
      item?.suggested_destination ||
      item?.publish_platform ||
      ""
  )
    .trim()
    .toLowerCase();

  if (["linkedin", "linkedin_pages", "wordpress"].includes(value)) return value;

  const title = String(item?.title || item?.name || "").toLowerCase();
  const taskType = String(item?.task_type || "").toLowerCase();

  if (title.includes("wordpress") || title.includes("blog post") || title.includes("landing page")) {
    return "wordpress";
  }

  if (title.includes("linkedin") || taskType.includes("social")) {
    return "linkedin";
  }

  return "linkedin";
}

function canSaveGeneratedPreviewAsActionTask(item) {
  if (!item) return false;

  const taskType = String(item.task_type || "").toLowerCase();
  const id = String(item.id || "").toLowerCase();

  return (
    item.generated_action_task === true ||
    taskType === "plan_generated_output" ||
    taskType === "backend_generated_output" ||
    id.startsWith("generated-") ||
    id.startsWith("backend-")
  );
}

function buildGeneratedActionTaskPayload(item) {
  const content = itemBody(item);
  const destination = generatedPreviewSaveType(item);
  const projectId = itemProjectId(item);
  const projectName = itemProjectName(item);
  const projectStatusValue = itemProjectStatus(item) || "active";
  const title = cleanDisplayTitle(
    item?.action_task_title || item?.title || item?.name,
    destination === "wordpress" ? "WordPress content task" : "LinkedIn content task"
  );
  const sourceTaskId = String(item?.source_task_id || item?.task_id || item?.id || "").trim();
  const sourceTitle = cleanDisplayTitle(
    item?.source_title || item?.parent_title || item?.original_title || item?.source_task_title,
    "Source plan"
  );

  const metadata = {
    source: {
      type: "workflow_generated_preview",
      task_id: sourceTaskId || null,
      title: sourceTitle,
    },
    generated_from: {
      task_id: sourceTaskId || null,
      title: sourceTitle,
    },
    generated_from_plan: true,
    generated_action_task: true,
    source_task_id: sourceTaskId || null,
    source_title: sourceTitle,
    parent_task_id: sourceTaskId || null,
    parent_title: sourceTitle,
    generated_at: new Date().toISOString(),
    destination,
    project_id: projectId || null,
    project_name: projectName || null,
    project_status: projectStatusValue,
    project: projectId || projectName ? {
      project_id: projectId || null,
      name: projectName || null,
      status: projectStatusValue,
    } : null,
  };

  return {
    title,
    name: title,
    project_id: projectId || undefined,
    project_name: projectName || undefined,
    project_status: projectStatusValue,
    content,
    output: content,
    result: content,
    status: "ready",
    task_type: item?.action_task_type || "publish_social_content",
    tool_key: item?.action_task_tool_key || "social",
    tool: item?.action_task_tool_key || "social",
    publish_platform: destination,
    publishing_platform: destination,
    destination,
    source_task_id: sourceTaskId || null,
    parent_task_id: sourceTaskId || null,
    source_title: sourceTitle,
    parent_title: sourceTitle,
    generated_from_plan: true,
    metadata,
  };
}

function parseOrdeneliDate(value) {
  if (!value) return 0;

  const raw = String(value).trim();
  const normalized = raw.includes("T") ? raw : `${raw.replace(" ", "T")}Z`;
  const date = new Date(normalized);

  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function actionTaskDateScore(item) {
  const metadata = safeJsonObject(item?.metadata);
  const publish = safeJsonObject(metadata?.publish);
  const schedule = safeJsonObject(metadata?.schedule);
  const status = normalizeStatus(item?.status);

  const candidates =
    status === "published"
      ? [item?.published_at, publish?.published_at, item?.updated_at, item?.created_at]
      : status === "scheduled"
        ? [item?.scheduled_for, schedule?.scheduled_for, item?.updated_at, item?.created_at]
        : [item?.updated_at, item?.created_at, item?.published_at, item?.scheduled_for];

  return Math.max(...candidates.map(parseOrdeneliDate), 0);
}

function actionTaskId(item) {
  return String(item?.task_id || item?.id || "").trim();
}

function normalizeTaskTitleForMatch(value) {
  return cleanDisplayTitle(value || "", "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function sourceTaskIdForItem(item) {
  const metadata = safeJsonObject(item?.metadata);
  const source = safeJsonObject(metadata?.source);
  const generatedFrom = safeJsonObject(metadata?.generated_from);
  const save = safeJsonObject(metadata?.save);

  return String(
    item?.source_task_id ||
      item?.parent_task_id ||
      item?.generated_from_task_id ||
      metadata?.source_task_id ||
      metadata?.parent_task_id ||
      metadata?.generated_from_task_id ||
      source?.task_id ||
      generatedFrom?.task_id ||
      save?.source_task_id ||
      ""
  ).trim();
}

function sourceTaskTitleForItem(item) {
  const metadata = safeJsonObject(item?.metadata);
  const source = safeJsonObject(metadata?.source);
  const generatedFrom = safeJsonObject(metadata?.generated_from);

  const explicitTitle =
    item?.source_title ||
    item?.parent_title ||
    metadata?.source_title ||
    metadata?.parent_title ||
    source?.title ||
    generatedFrom?.title ||
    "";

  if (String(explicitTitle || "").trim()) {
    return String(explicitTitle).trim();
  }

  const title = itemTitle(item, "");

  return title
    .replace(/^(?:GBP|LinkedIn|WordPress|Blog)(?:\s+posts?|\s+post|\s+landing\s+page|\s+drafts?)?\s*[-–—:]\s*/i, "")
    .trim();
}

function isGeneratedPublishingChildTask(item) {
  const metadata = safeJsonObject(item?.metadata);
  const title = itemTitle(item, "");
  const taskType = String(item?.task_type || "").toLowerCase();

  return (
    Boolean(sourceTaskIdForItem(item)) ||
    Boolean(metadata?.generated_from_plan) ||
    Boolean(metadata?.generated_action_task) ||
    Boolean(metadata?.source_task_id) ||
    (taskType === "publish_social_content" && /^(GBP|LinkedIn|WordPress|Blog)\b/i.test(title))
  );
}

function orderActionTasksForDisplay(allTasks, activeTab) {
  const visible = (Array.isArray(allTasks) ? allTasks : []).filter((item) => {
    if (activeTab === "all") return true;
    return normalizeStatus(item?.status) === activeTab;
  });

  const flatNewestFirstTabs = ["scheduled", "published", "completed", "failed", "archived"];

  if (flatNewestFirstTabs.includes(activeTab)) {
    return [...visible].sort((a, b) => actionTaskDateScore(b) - actionTaskDateScore(a));
  }

  const byId = new Map();

  visible.forEach((item) => {
    const id = actionTaskId(item);
    if (id) byId.set(id, item);
  });

  const parentCandidates = visible.filter((item) => !isGeneratedPublishingChildTask(item));

  const findParentForChild = (child) => {
    const sourceId = sourceTaskIdForItem(child);

    if (sourceId && byId.has(sourceId)) {
      const directParent = byId.get(sourceId);
      return itemsShareProjectForGrouping(child, directParent) ? directParent : null;
    }

    const sourceTitle = normalizeTaskTitleForMatch(sourceTaskTitleForItem(child));

    if (!sourceTitle) return null;

    return (
      parentCandidates.find((parent) => {
        const parentTitle = normalizeTaskTitleForMatch(itemTitle(parent, ""));

        return (
          parentTitle &&
          itemsShareProjectForGrouping(child, parent) &&
          (sourceTitle === parentTitle ||
            sourceTitle.includes(parentTitle) ||
            parentTitle.includes(sourceTitle))
        );
      }) || null
    );
  };

  const groups = new Map();

  const ensureGroup = (parent) => {
    const key = actionTaskId(parent) || normalizeTaskTitleForMatch(itemTitle(parent, ""));

    if (!groups.has(key)) {
      groups.set(key, { parent, children: [] });
    }

    return groups.get(key);
  };

  visible.forEach((item) => {
    const parent = isGeneratedPublishingChildTask(item) ? findParentForChild(item) : null;

    if (parent && actionTaskId(parent) !== actionTaskId(item)) {
      const group = ensureGroup(parent);

      group.children.push({
        ...item,
        _isNestedActionTask: true,
        _parentTitle: itemTitle(parent, "Source task"),
      });

      return;
    }

    ensureGroup(item);
  });

  return Array.from(groups.values())
    .map((group) => {
      const children = [...group.children].sort((a, b) => actionTaskDateScore(b) - actionTaskDateScore(a));
      const groupDate = Math.max(actionTaskDateScore(group.parent), ...children.map(actionTaskDateScore), 0);

      return { ...group, children, groupDate };
    })
    .sort((a, b) => b.groupDate - a.groupDate)
    .flatMap((group) => [group.parent, ...group.children]);
}

function PreviewModal({ item, onClose, onSaveAsActionTask, savingActionTask }) {
  if (!item) return null;

  const content = item.content || item.output || item.result || "";
  const title = cleanDisplayTitle(item.title || item.name, "Preview");
  const liveUrl = publishedUrlForItem(item);
  const canSaveAsActionTask = canSaveGeneratedPreviewAsActionTask(item) && Boolean(content);

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 print:hidden"
      onClick={onClose}
    >
      <div
        className="absolute right-0 top-0 h-full w-full max-w-3xl bg-white overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] p-5 flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
              Preview
            </p>

            <h2 className="font-display text-2xl font-black mt-1">{title}</h2>

            <div className="mt-3 flex flex-wrap gap-2">
              <StatusPill status={item.status} />
              {item.tool_key && <StatusPill status={item.tool_key} />}
              {item.task_type && <StatusPill status={item.task_type} />}
            </div>
          </div>

          <div className="flex gap-2">
            {liveUrl && (
              <button
                type="button"
                onClick={() => window.open(liveUrl, "_blank", "noopener,noreferrer")}
                className="bg-[#002FA7] text-white px-3 py-2 text-sm font-semibold hover:bg-[#002080] inline-flex items-center gap-1.5"
              >
                <Eye size={14} weight="bold" /> {publishedLinkLabel(item)}
              </button>
            )}

            {content && (
              <button
                onClick={() => copyText(content, "Copied content.")}
                className="border border-[#E5E7EB] px-3 py-2 text-sm font-semibold hover:bg-[#F5F5F5] inline-flex items-center gap-1.5"
              >
                <Copy size={14} weight="bold" /> Copy
              </button>
            )}

            {canSaveAsActionTask && (
              <button
                onClick={() => onSaveAsActionTask?.(item)}
                disabled={savingActionTask}
                className="bg-[#0A0A0A] text-white px-3 py-2 text-sm font-semibold hover:bg-black inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Plus size={14} weight="bold" /> {savingActionTask ? "Saving..." : "Save as action task"}
              </button>
            )}

            <button
              onClick={onClose}
              className="border border-[#E5E7EB] px-3 py-2 text-sm font-semibold hover:bg-[#F5F5F5]"
            >
              Close
            </button>
          </div>
        </div>

        <div className="p-5 sm:p-7">
          {content ? (
            <article
              className="prose-tight text-[#0A0A0A] text-[15px] leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: renderMarkdown(content),
              }}
            />
          ) : (
            <p className="text-sm text-[#737373]">No content to preview.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalShell({ open, onClose, title, subtitle, children, footer, wide = false }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className={`w-full ${wide ? "max-w-3xl" : "max-w-lg"} bg-white border border-[#E5E7EB] shadow-2xl max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-[#E5E7EB] px-5 py-4">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
            {subtitle}
          </p>

          <h3 className="font-display text-2xl font-black mt-1">{title}</h3>
        </div>

        <div className="px-5 py-5">{children}</div>

        <div className="border-t border-[#E5E7EB] px-5 py-4 flex justify-end gap-3 sticky bottom-0 bg-white">
          {footer}
        </div>
      </div>
    </div>
  );
}

function WorkflowCreateModal({ open, onClose, onSubmit, loading, initialWorkflow = null, mode = "create", projects = [], viewOnly = false }) {
  const firstPaidOrSocial = TOOLS.find((tool) => tool.key === "social") || TOOLS[0];
  const activeProjects = projects.filter((project) => !isArchivedProject(project));
  const defaultProjectId = activeProjects[0]?.project_id || "";
  const [form, setForm] = useState({
    project_id: defaultProjectId,
    project_name: activeProjects[0]?.name || "",
    name: "Weekly social content approval",
    tool_key: firstPaidOrSocial?.key || "social",
    frequency: "weekly",
    day_of_week: "MO",
    run_time: "09:00",
    approval_required: true,
    action_task_enabled: true,
    inputs: {},
  });

  useEffect(() => {
    if (!open) return;

    if (initialWorkflow) {
      const nextForm = workflowToForm(initialWorkflow);
      if (!nextForm.project_id && defaultProjectId) {
        nextForm.project_id = defaultProjectId;
        nextForm.project_name = activeProjects[0]?.name || "";
      }
      setForm(nextForm);
      return;
    }

    setForm({
      project_id: defaultProjectId,
      project_name: activeProjects[0]?.name || "",
      name: "Weekly social content approval",
      tool_key: firstPaidOrSocial?.key || "social",
      frequency: "weekly",
      day_of_week: "MO",
      run_time: "09:00",
      approval_required: true,
      action_task_enabled: true,
      inputs: {},
    });
  }, [open, initialWorkflow, firstPaidOrSocial?.key, defaultProjectId]);

  const selectedTool = getToolByKey(form.tool_key) || TOOLS[0];
  const visibleToolFields = (selectedTool?.fields || []).filter(
    (field) => String(field?.name || "").trim().toLowerCase() !== "platform"
  );
  const selectedProject = projects.find((project) => project.project_id === form.project_id) || null;
  const selectedProjectArchived = selectedProject ? isArchivedProject(selectedProject) : false;
  const projectOptions = selectedProject && selectedProjectArchived
    ? [selectedProject, ...activeProjects.filter((project) => project.project_id !== selectedProject.project_id)]
    : activeProjects;

  const updateInput = (fieldName, value) => {
    setForm((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        [fieldName]: value,
      },
    }));
  };

  const submit = () => {
    if (!String(form.name || "").trim()) {
      toast.error("Please enter a workflow name.");
      return;
    }

    if (!String(form.project_id || "").trim()) {
      toast.error("Please choose an active project for this workflow.");
      return;
    }

    if (!selectedProject || selectedProjectArchived || viewOnly) {
      toast.error("This workflow must use an active project. Archived projects are view-only.");
      return;
    }

    const missing = visibleToolFields.filter(
      (field) => field.required && !String(form.inputs?.[field.name] || "").trim()
    );

    if (missing.length) {
      toast.error(`Please fill: ${missing.map((field) => field.label).join(", ")}`);
      return;
    }

    onSubmit({
      ...form,
      project_name: selectedProject?.name || form.project_name || "",
      inputs: cleanInputObject(form.inputs),
    });
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "View / edit workflow" : "Create workflow"}
      subtitle="Automation setup"
      wide
      footer={
        <>
          <button
            onClick={onClose}
            className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5]"
          >
            Cancel
          </button>

          <button
            onClick={submit}
            disabled={loading || viewOnly || activeProjects.length === 0}
            className="bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#002080] inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={14} weight="bold" /> {loading ? (mode === "edit" ? "Saving..." : "Creating...") : viewOnly ? "View only" : activeProjects.length === 0 ? "No active project" : (mode === "edit" ? "Save workflow" : "Create workflow")}
          </button>
        </>
      }
    >
      {viewOnly && (
        <div className="mb-5 border border-[#FECACA] bg-[#FEF2F2] p-4 text-sm text-[#B91C1C]">
          This workflow belongs to an archived project. You can view it and its history, but you cannot edit, run, schedule or publish it until the project is restored.
        </div>
      )}

      {!viewOnly && activeProjects.length === 0 && (
        <div className="mb-5 border border-[#FDE68A] bg-[#FFFBEB] p-4 text-sm text-[#92400E]">
          Add or restore an active project first. Workflows must be attached to an active project.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Project <span className="text-[#DC2626]">*</span></span>
          <select
            value={form.project_id || ""}
            onChange={(e) => {
              const project = projects.find((item) => item.project_id === e.target.value) || null;
              setForm((prev) => ({
                ...prev,
                project_id: e.target.value,
                project_name: project?.name || "",
              }));
            }}
            disabled={viewOnly || projectOptions.length === 0}
            className="border border-[#E5E7EB] px-3 py-3 text-sm outline-none focus:border-[#002FA7] bg-white disabled:opacity-60"
          >
            <option value="">Select active project</option>
            {projectOptions.map((project) => (
              <option key={project.project_id} value={project.project_id}>
                {project.name}{isArchivedProject(project) ? " (archived)" : ""}
              </option>
            ))}
          </select>
          <span className="text-xs text-[#737373]">
            Workflows attached to archived projects become view-only.
          </span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Workflow name</span>
          <input
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            disabled={viewOnly}
            className="input-line"
            placeholder="Weekly social content approval"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Tool</span>
          <select
            value={form.tool_key}
            disabled={viewOnly}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                tool_key: e.target.value,
                inputs: {},
              }))
            }
            className="border border-[#E5E7EB] px-3 py-3 text-sm outline-none focus:border-[#002FA7] bg-white disabled:opacity-60"
          >
            {TOOLS.map((tool) => (
              <option key={tool.key} value={tool.key}>
                {tool.name}
              </option>
            ))}
          </select>
          <span className="text-xs text-[#737373]">
            Choose which AI tool this workflow should run. Schedule details stay managed in the background.
          </span>
        </label>
      </div>

      <div className="mt-5 border-t border-[#E5E7EB] pt-5">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373] mb-3">
          Inputs for {selectedTool?.name}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleToolFields.map((field) => (
            <label
              key={field.name}
              className={`flex flex-col gap-1.5 ${field.type === "textarea" ? "md:col-span-2" : ""}`}
            >
              <span className="text-sm font-semibold">
                {field.label} {field.required && <span className="text-[#DC2626]">*</span>}
              </span>

              {field.type === "textarea" ? (
                <textarea
                  value={form.inputs?.[field.name] || ""}
                  onChange={(e) => updateInput(field.name, e.target.value)}
                  disabled={viewOnly}
                  rows={4}
                  placeholder={field.placeholder}
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#002FA7]"
                />
              ) : (
                <input
                  value={form.inputs?.[field.name] || ""}
                  onChange={(e) => updateInput(field.name, e.target.value)}
                  disabled={viewOnly}
                  placeholder={field.placeholder}
                  className="input-line"
                />
              )}
            </label>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="border border-[#E5E7EB] p-4 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.approval_required}
            disabled={viewOnly}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, approval_required: e.target.checked }))
            }
            className="mt-1"
          />
          <span>
            <span className="block font-semibold text-sm">Require approval</span>
            <span className="block text-xs text-[#737373] mt-1">
              Generated drafts go into the approval inbox first.
            </span>
          </span>
        </label>

        <label className="border border-[#E5E7EB] p-4 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.action_task_enabled}
            disabled={viewOnly}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, action_task_enabled: e.target.checked }))
            }
            className="mt-1"
          />
          <span>
            <span className="block font-semibold text-sm">Create action task</span>
            <span className="block text-xs text-[#737373] mt-1">
              Approved drafts become ready-to-schedule tasks.
            </span>
          </span>
        </label>
      </div>
    </ModalShell>
  );
}

function WorkflowRunsModal({ open, onClose, workflow, runs, loading }) {
  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={workflow ? `${cleanDisplayTitle(workflowName(workflow), "Workflow")} runs` : "Workflow runs"}
      subtitle="Run history"
      wide
      footer={
        <button
          onClick={onClose}
          className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5]"
        >
          Close
        </button>
      }
    >
      {loading ? (
        <div className="grid gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-[#F5F5F5] animate-pulse" />
          ))}
        </div>
      ) : runs.length === 0 ? (
        <EmptyState
          title="No runs yet."
          text="Run this workflow manually or wait for the next scheduled cron execution."
        />
      ) : (
        <>
          <p className="text-xs text-[#737373] mb-3">
            Showing the latest {runs.length} workflow runs. Older failed runs from backend testing can stay in the database, but they do not mean the current workflow is broken.
          </p>
          <div className="divide-y divide-[#E5E7EB] border border-[#E5E7EB]">
          {runs.map((run, index) => (
            <div key={run.run_id || run.workflow_run_id || run.id || index} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <StatusPill status={run.status} />
                    {run.processed !== undefined && <StatusPill status={`${run.processed} processed`} />}
                  </div>
                  <p className="font-semibold text-sm">
                    {formatDate(run.created_at || run.started_at || run.run_at)}
                  </p>
                  {run.message && <p className="text-sm text-[#525252] mt-1">{run.message}</p>}
                  {run.error && <p className="text-sm text-[#B91C1C] mt-1">{run.error}</p>}
                </div>
              </div>
            </div>
          ))}
          </div>
        </>
      )}
    </ModalShell>
  );
}

function accountId(account) {
  return account?.id || account?.account_id || account?.connected_account_id || null;
}

function accountLabel(account) {
  return account?.account_name || account?.name || account?.metadata?.site_url || account?.platform_name || "Connected account";
}

function itemBody(item) {
  return item?.content || item?.output || item?.result || "";
}

function itemTitle(item, fallback = "Ordeneli draft") {
  return cleanDisplayTitle(item?.title || item?.name, fallback);
}

function stripInternalPostLabelFromTitle(value, fallback = "Ordeneli draft") {
  return cleanDisplayTitle(value || fallback, fallback)
    .replace(/\s+(?:[-–—:]\s*)?Post\s*\d+\s*$/i, "")
    .replace(/\s{2,}/g, " ")
    .trim() || fallback;
}

function publicPublishingTitle(item, fallback = "Ordeneli draft") {
  return stripInternalPostLabelFromTitle(item?.title || item?.name, fallback);
}

function publishedUrlForItem(item) {
  const metadata = safeJsonObject(item?.metadata);
  const publish = safeJsonObject(metadata?.publish);

  return (
    item?.published_url ||
    item?.post_link ||
    item?.external_url ||
    item?.provider_url ||
    publish?.published_url ||
    publish?.post_link ||
    publish?.external_url ||
    publish?.provider_url ||
    null
  );
}

function publishPlatformForItem(item) {
  const metadata = safeJsonObject(item?.metadata);
  const publish = safeJsonObject(metadata?.publish);
  const schedule = safeJsonObject(metadata?.schedule);

  return String(
    item?.publish_platform ||
      item?.published_platform ||
      publish?.platform ||
      schedule?.platform ||
      metadata?.publish_platform ||
      metadata?.platform ||
      ""
  )
    .trim()
    .toLowerCase();
}

function publishedLinkLabel(item) {
  const platform = publishPlatformForItem(item);
  const metadata = safeJsonObject(item?.metadata);
  const publish = safeJsonObject(metadata?.publish);
  const postStatus = String(item?.post_status || publish?.post_status || "").toLowerCase();

  if (platform === "wordpress") {
    return postStatus === "draft" ? "View WordPress draft" : "View WordPress post";
  }

  if (platform === "linkedin_pages") {
    return "View LinkedIn Page post";
  }

  if (platform === "linkedin") {
    return "View LinkedIn post";
  }

  return "View published post";
}

function taskMedia(item) {
  const metadata = safeJsonObject(item?.metadata);
  const candidates = [
    item?.media,
    item?.image,
    metadata?.media,
    metadata?.image,
    metadata?.attached_media,
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      const url = String(candidate.url || candidate.public_url || candidate.src || candidate.file_url || "").trim();
      if (url) {
        return {
          ...candidate,
          url,
          public_url: candidate.public_url || url,
        };
      }
    }
  }

  return null;
}

function mediaUrl(media) {
  return String(media?.url || media?.public_url || media?.src || media?.file_url || "").trim();
}

function mergeTaskMedia(item, media) {
  if (!item) return item;

  const metadata = safeJsonObject(item?.metadata);
  const nextMetadata = { ...metadata };

  if (media) {
    nextMetadata.media = media;
    nextMetadata.image = media;
  } else {
    delete nextMetadata.media;
    delete nextMetadata.image;
  }

  return {
    ...item,
    metadata: nextMetadata,
    media: media || null,
    image: media || null,
  };
}

function itemsShareProjectForGrouping(child, parent) {
  const childProjectId = itemProjectId(child);
  const parentProjectId = itemProjectId(parent);

  if (childProjectId && parentProjectId && childProjectId !== parentProjectId) {
    return false;
  }

  const childProjectName = itemProjectName(child);
  const parentProjectName = itemProjectName(parent);
  const cleanChildName = isGenericProjectLabel(childProjectName) ? "" : childProjectName.toLowerCase();
  const cleanParentName = isGenericProjectLabel(parentProjectName) ? "" : parentProjectName.toLowerCase();

  if (cleanChildName && cleanParentName && cleanChildName !== cleanParentName) {
    return false;
  }

  return true;
}

function TaskMediaPanel({
  item,
  disabled = false,
  uploading = false,
  removing = false,
  onUpload,
  onRemove,
  context = "preview",
  showPreviewImage = true,
}) {
  if (!item) return null;

  const media = taskMedia(item);
  const imageUrl = mediaUrl(media);
  const inputId = `task-media-${context}-${actionTaskId(item) || item?.id || "item"}`;
  const isBusy = uploading || removing;

  return (
    <div className="mb-4 border border-[#E5E7EB] bg-white p-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">
            Optional image
          </p>
          <p className="text-xs text-[#737373] mt-1">
            JPG, PNG or WebP. Images are checked before they are attached.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <label
            htmlFor={inputId}
            className={`border px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5 ${
              disabled || isBusy
                ? "opacity-50 cursor-not-allowed border-[#E5E7EB] text-[#737373]"
                : "cursor-pointer border-[#002FA7] text-[#002FA7] hover:bg-[#EFF6FF]"
            }`}
          >
            <Plus size={14} weight="bold" />
            {uploading ? "Checking..." : imageUrl ? "Replace image" : "Upload image"}
          </label>

          <input
            id={inputId}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            disabled={disabled || isBusy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";

              if (file) {
                onUpload?.(item, file);
              }
            }}
          />

          {imageUrl && (
            <button
              type="button"
              onClick={() => onRemove?.(item)}
              disabled={disabled || isBusy}
              className="border border-[#DC2626] text-[#DC2626] px-3 py-2 text-xs font-bold hover:bg-[#FEF2F2] disabled:opacity-50"
            >
              {removing ? "Removing..." : "Remove"}
            </button>
          )}
        </div>
      </div>

      {showPreviewImage && (
        imageUrl ? (
          <div className="mt-4 overflow-hidden border border-[#E5E7EB] bg-[#F5F5F5]">
            <img
              src={imageUrl}
              alt={media?.original_name || "Attached marketing visual"}
              className="max-h-80 w-full object-contain bg-white"
            />
          </div>
        ) : (
          <div className="mt-4 border border-dashed border-[#D4D4D4] bg-[#F5F5F5] px-4 py-6 text-sm text-[#737373]">
            No image attached. You can continue with text only.
          </div>
        )
      )}

      {disabled && (
        <p className="mt-3 text-xs text-[#B91C1C]">
          Images cannot be changed while this task belongs to an archived project.
        </p>
      )}
    </div>
  );
}

function accountType(account) {
  return String(account?.account_type || account?.metadata?.account_type || "")
    .trim()
    .toLowerCase();
}

function isLinkedInPageAccount(account) {
  if (account?.platform !== "linkedin") return false;

  const type = accountType(account);
  const connectedVia = String(account?.metadata?.connected_via || "").toLowerCase();

  return (
    ["organization", "company", "company_page", "linkedin_page", "page"].includes(type) ||
    connectedVia === "linkedin_pages_oauth" ||
    Boolean(account?.metadata?.organization_id || account?.metadata?.organization_urn)
  );
}

function isLinkedInProfileAccount(account) {
  return account?.platform === "linkedin" && !isLinkedInPageAccount(account);
}

function publishingDestinationLabel(platform, account = null) {
  const normalized = String(platform || "").trim().toLowerCase();

  if (normalized === "wordpress") return "WordPress";
  if (normalized === "linkedin_pages") return "LinkedIn Page";
  if (normalized === "linkedin" && isLinkedInPageAccount(account)) return "LinkedIn Page";
  if (normalized === "linkedin") return "LinkedIn";

  return normalized ? cleanDisplayTitle(normalized.replace(/_/g, " "), "Destination") : "Destination";
}

function taskToolKey(item) {
  return String(item?.tool_key || item?.tool || item?.task_type || "")
    .trim()
    .toLowerCase();
}

function isSocialActionTask(item) {
  const key = taskToolKey(item);
  const taskType = String(item?.task_type || "").trim().toLowerCase();

  return (
    key === "social" ||
    key === "social_ai" ||
    key === "social_media" ||
    key === "publish_social_content" ||
    taskType === "publish_social_content" ||
    taskType.includes("social")
  );
}

function scheduledPostIndexForItem(item) {
  const metadata = safeJsonObject(item?.metadata);
  const schedule = safeJsonObject(metadata?.schedule);
  const raw =
    item?.selected_post_index ??
    metadata?.selected_post_index ??
    schedule?.selected_post_index ??
    null;

  if (raw === null || raw === undefined || raw === "") return 0;

  const index = Number(raw);
  return Number.isFinite(index) && index >= 0 ? index : 0;
}

function scheduledCtaUrlForItem(item) {
  const metadata = safeJsonObject(item?.metadata);
  const schedule = safeJsonObject(metadata?.schedule);

  return String(
    item?.cta_url ||
      schedule?.cta_url ||
      schedule?.link_url ||
      metadata?.cta_url ||
      metadata?.link_url ||
      schedule?.selected_post?.cta_url ||
      metadata?.selected_post?.cta_url ||
      ""
  ).trim();
}

function selectedScheduledTextForItem(item) {
  const metadata = safeJsonObject(item?.metadata);
  const schedule = safeJsonObject(metadata?.schedule);

  return String(
    schedule?.selected_post_text ||
      metadata?.selected_post_text ||
      schedule?.selected_post?.text ||
      schedule?.selected_post?.content ||
      metadata?.selected_post?.text ||
      metadata?.selected_post?.content ||
      ""
  ).trim();
}

function visibleTaskBody(item) {
  const selectedText = selectedScheduledTextForItem(item);
  return selectedText || itemBody(item);
}

function destinationForScheduledTask(item, fallback = "") {
  const metadata = safeJsonObject(item?.metadata);
  const schedule = safeJsonObject(metadata?.schedule);

  return String(
    item?.publish_platform ||
      item?.published_platform ||
      schedule?.platform ||
      metadata?.publish_platform ||
      metadata?.platform ||
      fallback ||
      ""
  )
    .trim()
    .toLowerCase();
}

function titleFromSelectedPart(part, item, fallback = "Ordeneli marketing draft") {
  const raw = String(part?.raw || part?.text || "").replace(/\r\n/g, "\n");
  const hookMatch = raw.match(/(?:^|\n)\s*(?:\*\*)?Hook(?:\*\*)?\s*:\s*(.+)/i);

  if (hookMatch?.[1]) {
    return cleanDisplayTitle(hookMatch[1].replace(/\*\*/g, "").trim(), fallback);
  }

  const cleaned = String(part?.text || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.replace(/^#+\s*/, "").replace(/\*\*/g, "").trim())
    .find((line) => line && !/^hashtags?\s*:/i.test(line) && !/^#/.test(line));

  if (cleaned && cleaned.length <= 120) {
    return cleanDisplayTitle(cleaned, fallback);
  }

  return publicPublishingTitle(item, fallback);
}

function appendCtaUrlForPreview(text, ctaUrl) {
  const cleanText = String(text || "").trim();
  let cleanUrl = String(ctaUrl || "").trim();

  if (!cleanUrl) return cleanText;

  if (!/^https?:\/\//i.test(cleanUrl)) {
    cleanUrl = `https://${cleanUrl}`;
  }

  if (cleanText.includes(cleanUrl)) return cleanText;

  return `${cleanText}

${cleanUrl}`.trim();
}

function isInvalidGeneratedPublishingText(text) {
  const value = String(text || "").trim();

  if (!value) return false;

  const lower = value.toLowerCase();

  return (
    /which\s+platform\s+should\s+i\s+tailor/i.test(value) ||
    /which\s+platform\s+would\s+you\s+like/i.test(value) ||
    /please\s+(specify|provide|choose).{0,80}platform/i.test(value) ||
    /\b(tiktok|facebook|twitter|instagram|x\/twitter)\b/i.test(value)
  );
}

function compactBlankLines(text) {
  return String(text || "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function stripPostDayLabel(rawTitle, index) {
  const text = String(rawTitle || "").replace(/^#+\s*/, "").replace(/\*\*/g, "").trim();
  const match = text.match(/post\s*(\d+)/i);
  return match ? `Post ${match[1]}` : `Post ${index + 1}`;
}

function cleanGeneratedPostText(raw, type = "linkedin") {
  const lines = String(raw || "").replace(/\r\n/g, "\n").split("\n");
  const cleaned = [];

  for (const originalLine of lines) {
    let line = String(originalLine || "").trim();

    if (!line) {
      cleaned.push("");
      continue;
    }

    line = line.replace(/^\s*[-–—]{2,}\s*$/, "");
    if (!line) continue;

    line = line.replace(/^#+\s*/, "").replace(/\*\*/g, "").trim();

    if (/^post\s*\d+\b/i.test(line)) continue;
    if (/^best\s*time\s*:/i.test(line)) continue;

    const labelMatch = line.match(/^(hook|body|caption|post\s*text|text|hashtags?)\s*:\s*(.*)$/i);
    if (labelMatch) {
      line = labelMatch[2].trim();
      if (!line) continue;
    }

    if (type === "linkedin") {
      line = line.replace(/<[^>]*>/g, "");
    }

    cleaned.push(line);
  }

  return compactBlankLines(cleaned.join("\n"));
}

function splitGeneratedContent(content, type = "linkedin") {
  const text = String(content || "").replace(/\r\n/g, "\n").trim();

  if (!text) return [];

  const regex = /(^|\n)\s*(?:#{1,6}\s*)?(?:\*\*)?Post\s*(\d+)\s*(?:[-:–—]\s*[^\n*]+)?(?:\*\*)?\s*(?=\n|$)/gi;
  const matches = [...text.matchAll(regex)];

  if (!matches.length) {
    return [
      {
        id: "single",
        title: type === "wordpress" ? "Draft" : "Post 1",
        text: cleanGeneratedPostText(text, type),
        raw: text,
      },
    ];
  }

  return matches
    .map((match, index) => {
      const headingStart = match.index + (match[1] ? match[1].length : 0);
      const bodyStart = match.index + match[0].length;
      const nextStart = matches[index + 1]?.index ?? text.length;
      const raw = text.slice(bodyStart, nextStart).trim();
      const cleaned = cleanGeneratedPostText(raw, type);

      return {
        id: `post-${match[2] || index + 1}`,
        title: stripPostDayLabel(text.slice(headingStart, bodyStart), index),
        text: cleaned,
        raw,
      };
    })
    .filter((part) => part.text);
}

function publishingPartsForItem(item, type = "linkedin") {
  const parts = splitGeneratedContent(itemBody(item), type);
  return parts.length ? parts : [{ id: "single", title: "Post 1", text: itemBody(item), raw: itemBody(item) }];
}

function hasMultiplePublishingParts(item, type = "linkedin") {
  return publishingPartsForItem(item, type).length > 1;
}

function PublishingPreviewModal({
  preview,
  accounts,
  selectedAccountId,
  onAccountChange,
  loading,
  onClose,
  onConfirm,
  mediaUploading = false,
  mediaRemoving = false,
  onUploadMedia,
  onRemoveMedia,
  mediaDisabled = false,
}) {
  const [selectedPartIndex, setSelectedPartIndex] = useState(0);
  const [ctaUrl, setCtaUrl] = useState("");

  useEffect(() => {
    if (!preview?.item) {
      setSelectedPartIndex(0);
      setCtaUrl("");
      return;
    }

    setSelectedPartIndex(scheduledPostIndexForItem(preview.item));
    setCtaUrl(scheduledCtaUrlForItem(preview.item));
  }, [preview?.item?.task_id, preview?.item?.approval_id, preview?.item?.id, preview?.type]);

  if (!preview?.item || !preview?.type) return null;

  const item = preview.item;
  const type = preview.type;
  const isLinkedIn = type === "linkedin";
  const availableAccounts = Array.isArray(accounts) ? accounts : [];
  const fallbackAccount = availableAccounts[0] || null;
  const selectedAccount =
    availableAccounts.find((entry) => accountId(entry) === selectedAccountId) || fallbackAccount;

  const accountName = accountLabel(selectedAccount);
  const accountMeta =
    selectedAccount?.provider_account_id ||
    selectedAccount?.metadata?.site_url ||
    selectedAccount?.metadata?.username ||
    "Connected account";

  const parts = publishingPartsForItem(item, type);
  const selectedPart = parts[selectedPartIndex] || parts[0];
  const baseTitle = publicPublishingTitle(item, type === "wordpress" ? "Ordeneli marketing draft" : "Ordeneli post");
  const draftTitle = type === "wordpress" ? titleFromSelectedPart(selectedPart, item, baseTitle) : baseTitle;
  const previewText = selectedPart?.text || "";
  const previewTextWithCta = appendCtaUrlForPreview(previewText, ctaUrl);
  const invalidGeneratedPublishingText = isInvalidGeneratedPublishingText(previewText);

  return (
    <ModalShell
      open={Boolean(preview)}
      onClose={loading ? undefined : onClose}
      title={isLinkedIn ? "LinkedIn post preview" : "WordPress draft preview"}
      subtitle={isLinkedIn ? "Review one post before publishing" : "Review one draft before creating it"}
      wide
      footer={
        <>
          <button
            onClick={onClose}
            disabled={loading}
            className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(type, item, selectedPart, selectedAccount, { ctaUrl })}
            disabled={loading || !selectedAccount || !previewText.trim() || invalidGeneratedPublishingText}
            className="bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#002080] inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <PaperPlaneTilt size={14} weight="bold" /> {loading ? "Sending..." : isLinkedIn ? `Publish ${selectedPart.title}` : `Create ${selectedPart.title} draft`}
          </button>
        </>
      }
    >
      {invalidGeneratedPublishingText && (
        <div className="mb-4 border border-[#FECACA] bg-[#FEF2F2] p-3 text-sm text-[#B91C1C]">
          This generated text looks like an AI follow-up question or mentions an unsupported platform. Regenerate the draft before publishing.
        </div>
      )}

      {parts.length > 1 && (
        <div className="mb-4 border border-[#BFDBFE] bg-[#EFF6FF] p-3">
          <p className="text-sm font-semibold text-[#1E40AF]">
            This output contains {parts.length} separate posts. Choose one below — Ordeneli will only send the selected post, not the whole campaign.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {parts.map((part, index) => (
              <button
                key={part.id || index}
                onClick={() => setSelectedPartIndex(index)}
                className={`px-3 py-2 text-xs font-bold border ${
                  selectedPartIndex === index
                    ? "bg-[#002FA7] border-[#002FA7] text-white"
                    : "bg-white border-[#BFDBFE] text-[#1E40AF] hover:border-[#002FA7]"
                }`}
              >
                {part.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {availableAccounts.length > 1 && (
        <label className="mb-4 flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Publishing account</span>
          <select
            value={accountId(selectedAccount) || ""}
            onChange={(e) => onAccountChange(type, e.target.value)}
            className="border border-[#E5E7EB] px-3 py-3 text-sm outline-none focus:border-[#002FA7] bg-white"
          >
            {availableAccounts.map((entry) => (
              <option key={accountId(entry)} value={accountId(entry)}>
                {accountLabel(entry)}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="mb-4 flex flex-col gap-1.5">
        <span className="text-sm font-semibold">Optional link / CTA URL</span>
        <input
          value={ctaUrl}
          onChange={(e) => setCtaUrl(e.target.value)}
          placeholder="https://yourbusiness.com"
          className="border border-[#E5E7EB] px-3 py-3 text-sm outline-none focus:border-[#002FA7] bg-white"
        />
        <span className="text-xs text-[#737373]">
          Optional. For LinkedIn it is appended to the post text. For WordPress it becomes a simple link at the bottom of the draft.
        </span>
      </label>

      <TaskMediaPanel
        item={item}
        disabled={mediaDisabled}
        uploading={mediaUploading}
        removing={mediaRemoving}
        onUpload={onUploadMedia}
        onRemove={onRemoveMedia}
        context={`${type}-preview`}
        showPreviewImage={false}
      />

      {isLinkedIn ? (
        <div className="border border-[#D6E4F0] bg-white rounded-sm overflow-hidden shadow-sm">
          <div className="p-4 flex items-start gap-3 border-b border-[#E5E7EB]">
            <div className="h-12 w-12 rounded-full bg-[#0A66C2] text-white flex items-center justify-center font-display font-black">
              {String(accountName || "O").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-[#0A0A0A]">{accountName}</p>
              <p className="text-xs text-[#737373] break-all">{accountMeta}</p>
              <p className="text-xs text-[#737373] mt-0.5">Preview · Public post</p>
            </div>
          </div>
          <div className="p-5 whitespace-pre-line text-[15px] leading-relaxed text-[#0A0A0A]">
            {previewTextWithCta}
          </div>
          {mediaUrl(taskMedia(item)) && (
            <div className="px-5 pb-5">
              <img
                src={mediaUrl(taskMedia(item))}
                alt={taskMedia(item)?.original_name || "Attached marketing visual"}
                className="w-full max-h-96 object-contain border border-[#E5E7EB] bg-white"
              />
            </div>
          )}
          <div className="px-5 py-3 border-t border-[#E5E7EB] flex gap-6 text-xs text-[#737373] font-semibold">
            <span>Like</span><span>Comment</span><span>Repost</span><span>Send</span>
          </div>
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white">
          <div className="p-4 border-b border-[#E5E7EB] bg-[#F5F5F5]">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Draft destination</p>
            <p className="font-semibold mt-1">{accountName}</p>
            <p className="text-xs text-[#737373] break-all mt-1">{accountMeta}</p>
          </div>
          <div className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-2">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">WordPress draft title</p>
              {parts.length > 1 && (
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#002FA7] bg-[#EFF6FF] border border-[#BFDBFE] px-2 py-1">
                  Internal selection: {selectedPart.title}
                </span>
              )}
            </div>
            <h2 className="font-display text-3xl font-black tracking-tight">{draftTitle}</h2>
            <p className="mt-2 text-xs text-[#737373]">
              The Post number is only an Ordeneli selector label. It is not added to the live WordPress title.
            </p>
            {mediaUrl(taskMedia(item)) && (
              <div className="mt-5 border-t border-[#E5E7EB] pt-5">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373] mb-2">
                  Featured visual preview
                </p>
                <img
                  src={mediaUrl(taskMedia(item))}
                  alt={taskMedia(item)?.original_name || "Attached marketing visual"}
                  className="w-full max-h-96 object-contain border border-[#E5E7EB] bg-white"
                />
              </div>
            )}
            <div className="mt-5 border-t border-[#E5E7EB] pt-5 prose-tight text-[#0A0A0A] text-[15px] leading-relaxed" dangerouslySetInnerHTML={{ __html: renderMarkdown(previewTextWithCta) }} />
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function WordPressConnectModal({ open, onClose, onSubmit, loading }) {
  const [form, setForm] = useState({
    site_url: "",
    username: "",
    application_password: "",
    account_name: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm((prev) => ({
      site_url: prev.site_url || "",
      username: prev.username || "",
      application_password: "",
      account_name: prev.account_name || "",
    }));
  }, [open]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = () => {
    if (!form.site_url.trim()) {
      toast.error("WordPress site URL is required.");
      return;
    }
    if (!form.username.trim()) {
      toast.error("WordPress username is required.");
      return;
    }
    if (!form.application_password.trim()) {
      toast.error("Application password is required.");
      return;
    }
    onSubmit(form);
  };

  return (
    <ModalShell
      open={open}
      onClose={loading ? undefined : onClose}
      title="Connect WordPress"
      subtitle="Manual publishing connection"
      wide
      footer={
        <>
          <button
            onClick={onClose}
            disabled={loading}
            className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={loading}
            className="bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#002080] disabled:opacity-50"
          >
            {loading ? "Connecting..." : "Connect WordPress"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Site URL</span>
          <input
            value={form.site_url}
            onChange={(e) => update("site_url", e.target.value)}
            placeholder="https://blog.yourbusiness.com"
            className="input-line"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">WordPress username</span>
          <input
            value={form.username}
            onChange={(e) => update("username", e.target.value)}
            placeholder="admin or your WordPress username"
            className="input-line"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Application password</span>
          <input
            value={form.application_password}
            onChange={(e) => update("application_password", e.target.value)}
            placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
            type="password"
            className="input-line"
          />
          <span className="text-xs text-[#737373]">
            Use a WordPress Application Password, not your normal dashboard password.
          </span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-semibold">Display name</span>
          <input
            value={form.account_name}
            onChange={(e) => update("account_name", e.target.value)}
            placeholder="Your Business Blog"
            className="input-line"
          />
        </label>
      </div>
    </ModalShell>
  );
}

function NewsletterExportModal({ exportData, onClose }) {
  if (!exportData) return null;

  return (
    <ModalShell
      open={Boolean(exportData)}
      onClose={onClose}
      title="Newsletter exported"
      subtitle="Email copy package"
      wide
      footer={
        <>
          <button
            onClick={() => copyText(exportData.copy_blocks?.subject || exportData.subject, "Copied subject line.")}
            className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5]"
          >
            Copy subject
          </button>
          <button
            onClick={() => copyText(exportData.copy_blocks?.html || exportData.html, "Copied HTML email.")}
            className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5]"
          >
            Copy HTML
          </button>
          <button
            onClick={() => copyText(exportData.copy_blocks?.plain_text || exportData.text, "Copied plain text email.")}
            className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5]"
          >
            Copy text
          </button>
          <button onClick={onClose} className="btn-primary">
            Done
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="border border-[#E5E7EB] bg-[#F5F5F5] p-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Subject</p>
          <p className="font-semibold mt-1">{exportData.subject}</p>
          {exportData.preview_text && (
            <p className="text-sm text-[#525252] mt-2">{exportData.preview_text}</p>
          )}
        </div>

        {mediaUrl(exportData.media) && (
          <div className="border border-[#E5E7EB] bg-white p-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373] mb-2">
              Attached image
            </p>
            <img
              src={mediaUrl(exportData.media)}
              alt={exportData.media?.original_name || "Attached newsletter visual"}
              className="w-full max-h-80 object-contain bg-white border border-[#E5E7EB]"
            />
            <p className="text-xs text-[#737373] mt-2">
              This image is attached to the task preview. Add it manually to your email platform if your newsletter tool needs image upload separately.
            </p>
          </div>
        )}

        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373] mb-2">Email HTML preview</p>
          <iframe
            title="Newsletter HTML preview"
            srcDoc={exportData.copy_blocks?.html || exportData.html || ""}
            className="w-full h-96 border border-[#E5E7EB] bg-white"
          />
        </div>

        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373] mb-2">Plain text version</p>
          <pre className="whitespace-pre-wrap text-xs bg-[#0A0A0A] text-white p-4 overflow-x-auto max-h-72">
            {exportData.copy_blocks?.plain_text || exportData.text}
          </pre>
        </div>
      </div>
    </ModalShell>
  );
}

function ConfirmActionModal({ open, title, subtitle, message, confirmLabel, danger = false, loading, onClose, onConfirm }) {
  return (
    <ModalShell
      open={open}
      onClose={loading ? undefined : onClose}
      title={title}
      subtitle={subtitle || "Confirm action"}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={loading}
            className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`${danger ? "bg-[#DC2626] hover:bg-[#B91C1C]" : "bg-[#0A0A0A] hover:bg-black"} text-white px-4 py-2 text-sm font-semibold disabled:opacity-50`}
          >
            {loading ? "Working..." : confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-sm text-[#525252] leading-relaxed whitespace-pre-line">{message}</p>
    </ModalShell>
  );
}

function LinkedInPagePickerModal({
  state,
  onClose,
  onToggle,
  onSave,
  onRefresh,
}) {
  const pages = Array.isArray(state.pages) ? state.pages : [];
  const selectedCount = state.selectedIds.length;
  const limitLabel = state.unlimited ? "Unlimited" : `${selectedCount}/${state.limit}`;

  return (
    <ModalShell
      open={state.open}
      onClose={state.saving ? undefined : onClose}
      title="Choose LinkedIn Pages"
      subtitle="Connected publishing"
      wide
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={state.saving}
            className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onRefresh}
            disabled={state.loading || state.saving}
            className="border border-[#0A0A0A] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5] disabled:opacity-50"
          >
            Refresh pages
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={state.loading || state.saving || pages.length === 0 || selectedCount === 0}
            className="bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#002080] disabled:opacity-50"
          >
            {state.saving ? "Saving..." : "Save selected pages"}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="border border-[#E5E7EB] bg-[#FAFAFA] p-4">
          <p className="font-semibold">
            Select the LinkedIn Pages you want Ordeneli to keep connected.
          </p>
          <p className="text-sm text-[#525252] mt-2">
            This replaces your active LinkedIn Page selection. Existing active pages stay selected by default. Normal accounts are limited by plan; platform admins are unlimited.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <StatusPill status={state.unlimited ? "admin unlimited" : "plan limit"} />
            <span className="border border-[#E5E7EB] bg-white px-2 py-1 text-[10px] font-mono uppercase tracking-[0.16em] text-[#525252]">
              Selected: {limitLabel}
            </span>
          </div>
        </div>

        {state.loading ? (
          <div className="border border-[#E5E7EB] p-6 text-sm text-[#525252]">
            Loading LinkedIn Pages...
          </div>
        ) : pages.length === 0 ? (
          <div className="border border-dashed border-[#E5E7EB] bg-[#F5F5F5] p-6">
            <p className="font-display text-xl font-bold">No pages found for selection.</p>
            <p className="text-sm text-[#525252] mt-2">
              Click Connect LinkedIn Page again to refresh the pages returned by LinkedIn.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {pages.map((page) => {
              const id = accountId(page);
              const selected = state.selectedIds.includes(id);
              const status = normalizeStatus(page.status || "");
              const pending = status === "pending_selection";

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => onToggle(id)}
                  className={`text-left border p-4 transition ${
                    selected
                      ? "border-[#002FA7] bg-[#EFF6FF]"
                      : "border-[#E5E7EB] bg-white hover:border-[#93C5FD]"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`mt-1 h-5 w-5 shrink-0 border flex items-center justify-center ${
                      selected ? "bg-[#002FA7] border-[#002FA7] text-white" : "border-[#A3A3A3] bg-white"
                    }`}>
                      {selected ? "✓" : ""}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-display text-lg font-bold">{accountLabel(page)}</p>
                        <StatusPill status={pending ? "available" : page.status || "connected"} />
                      </div>
                      <p className="text-xs text-[#737373] mt-1 break-all">
                        {page.provider_account_id || page.metadata?.organization_urn || "LinkedIn Page"}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </ModalShell>
  );
}

function PublishingAccountColumn({ title, accounts, platform, slotLimit, unlimited = false, connecting, workingId, onConnect, onDisconnect }) {
  const filledAccounts = Array.isArray(accounts) ? accounts : [];
  const numericSlotLimit = Number(slotLimit || 0);
  const emptySlots = unlimited ? 0 : Math.max(0, numericSlotLimit - filledAccounts.length);
  const canConnect = unlimited || filledAccounts.length < numericSlotLimit;

  return (
    <div className="border border-[#E5E7EB] bg-white">
      <div className="p-4 border-b border-[#E5E7EB] flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">
            {title} accounts
          </p>
          <p className="font-display text-xl font-black mt-1">
            {unlimited ? `${filledAccounts.length} connected` : `${filledAccounts.length}/${numericSlotLimit} connected`}
          </p>
          {unlimited && (
            <p className="text-xs text-[#737373] mt-1">Admin unlimited</p>
          )}
        </div>
        <button
          onClick={onConnect}
          disabled={!canConnect || connecting}
          className="border border-[#0A0A0A] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
          title={canConnect ? `Connect ${title}` : `You have reached your ${title} account limit`}
        >
          {connecting ? "Connecting..." : canConnect ? `+ Connect ${title}` : "Limit reached"}
        </button>
      </div>

      <div className="divide-y divide-[#E5E7EB]">
        {filledAccounts.map((account, index) => {
          const id = accountId(account);
          return (
            <div key={id || `${platform}-${index}`} className="p-4 bg-[#F5F5F5]">
              <div className="flex items-center gap-2 mb-2">
                <StatusPill status={platform} />
                <StatusPill status={account.status || "connected"} />
              </div>
              <p className="font-display text-lg font-bold">{accountLabel(account)}</p>
              <p className="text-xs text-[#737373] mt-1 break-all">
                {account.provider_account_id || account.metadata?.site_url || "Connected account"}
              </p>
              {account.metadata?.username && (
                <p className="text-xs text-[#737373] mt-1">User: {account.metadata.username}</p>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => onDisconnect(account)}
                  disabled={workingId === `disconnect:${id}`}
                  className="border border-[#DC2626] text-[#DC2626] px-3 py-2 text-xs font-bold hover:bg-[#FEF2F2] disabled:opacity-50"
                >
                  {workingId === `disconnect:${id}` ? "Disconnecting..." : "Disconnect"}
                </button>
                {platform === "wordpress" && account.metadata?.site_url && (
                  <a
                    href={account.metadata.site_url}
                    target="_blank"
                    rel="noreferrer"
                    className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-white"
                  >
                    Open site
                  </a>
                )}
              </div>
            </div>
          );
        })}

        {Array.from({ length: emptySlots }).map((_, index) => (
          <div key={`empty-${platform}-${index}`} className="p-4 bg-white text-[#737373] italic flex items-center gap-2">
            <span className="h-4 w-4 rounded-full border border-[#0A0A0A] inline-block" />
            Empty slot
          </div>
        ))}
      </div>
    </div>
  );
}

const ACCOUNT_SLOT_ADDONS = [
  { key: "addon_accounts_plus_1", label: "+1 LinkedIn and +1 WordPress", price: "£5/mo", slots: 1 },
  { key: "addon_accounts_plus_2", label: "+2 LinkedIn and +2 WordPress", price: "£8/mo", slots: 2 },
  { key: "addon_accounts_plus_3", label: "+3 LinkedIn and +3 WordPress", price: "£10/mo", slots: 3 },
];
const PLAN_GENERATOR_BUTTON_CLASS =
  "border border-[#002FA7] text-[#002FA7] px-3 py-2 text-xs font-bold hover:bg-[#EFF6FF] inline-flex items-center gap-1.5 disabled:opacity-50 max-w-full whitespace-normal text-left leading-tight";

const PLAN_NEUTRAL_BUTTON_CLASS =
  "border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5] inline-flex items-center gap-1.5 disabled:opacity-50 max-w-full whitespace-normal text-left leading-tight";

export default function WorkflowCenter() {
  const { user, usage, refreshUsage } = useAuth();

  const currentPlan = normalizePlan(usage?.plan || user?.plan || "starter");
  const workflowUnlocked = planUnlocksWorkflows(currentPlan);

  const [workflows, setWorkflows] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [connectingId, setConnectingId] = useState(null);
  const [workflowTab, setWorkflowTab] = useState("active");
  const [approvalTab, setApprovalTab] = useState("pending");
  const [taskTab, setTaskTab] = useState("ready");
  const [preview, setPreview] = useState(null);
  const [publishingPreview, setPublishingPreview] = useState(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creatingWorkflow, setCreatingWorkflow] = useState(false);
  const [savingWorkflow, setSavingWorkflow] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, workflow: null });

  const [runsModal, setRunsModal] = useState({
    open: false,
    workflow: null,
    runs: [],
    loading: false,
  });

  const [rejectModal, setRejectModal] = useState({
    open: false,
    approvalId: null,
    title: "",
    feedback: "",
  });

  const emptyScheduleModal = {
    open: false,
    taskId: null,
    title: "",
    scheduledFor: "",
    task: null,
    selectedPartIndex: 0,
    destination: "linkedin",
    accountId: "",
    ctaUrl: "",
  };

  const [scheduleModal, setScheduleModal] = useState(emptyScheduleModal);

  const closeScheduleModal = () => {
    setScheduleModal(emptyScheduleModal);
  };

  const [archiveModal, setArchiveModal] = useState({
    open: false,
    taskId: null,
    title: "",
  });

  const [wordpressModalOpen, setWordpressModalOpen] = useState(false);
  const [linkedinPagePicker, setLinkedinPagePicker] = useState({
    open: false,
    loading: false,
    saving: false,
    pages: [],
    selectedIds: [],
    limit: 3,
    unlimited: false,
    connectedCount: 0,
    remaining: 0,
  });
  const [newsletterExport, setNewsletterExport] = useState(null);
  const [publishingAccountIds, setPublishingAccountIds] = useState({ linkedin: "", linkedin_pages: "", wordpress: "" });
  const [disconnectModal, setDisconnectModal] = useState({ open: false, account: null });

  const load = async () => {
    if (!workflowUnlocked) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [workflowRes, approvalRes, taskRes, healthRes, accountsRes, projectsRes] = await Promise.allSettled([
        axios.get(`${API}/workflows`),
        axios.get(`${API}/approvals`),
        axios.get(`${API}/action-tasks`),
        axios.get(`${API}/automation/health`),
        axios.get(`${API}/integrations/accounts`),
        axios.get(`${API}/projects`),
      ]);

      if (workflowRes.status === "fulfilled") {
        setWorkflows(readItems(workflowRes.value.data));
      }

      if (approvalRes.status === "fulfilled") {
        setApprovals(readItems(approvalRes.value.data));
      }

      if (taskRes.status === "fulfilled") {
        setTasks(readItems(taskRes.value.data));
      }

      if (healthRes.status === "fulfilled") {
        setHealth(
          healthRes.value.data?.health ||
            healthRes.value.data?.item ||
            healthRes.value.data ||
            null
        );
      }

      if (accountsRes.status === "fulfilled") {
        setConnectedAccounts(readItems(accountsRes.value.data));
      }

      if (projectsRes.status === "fulfilled") {
        setProjects(readItems(projectsRes.value.data));
      }

      if (
        workflowRes.status === "rejected" ||
        approvalRes.status === "rejected" ||
        taskRes.status === "rejected"
      ) {
        toast.error("Some workflow data could not be loaded.");
      }
    } catch {
      toast.error("Could not load workflow center.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workflowUnlocked) {
      load();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowUnlocked]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const integration = params.get("integration");
    const status = params.get("status");
    const message = params.get("message");

    if (!integration || !status) return;

    const readableMessage = message || `${integration} connection ${status}.`;

    if (status === "success") {
      toast.success(readableMessage);
    } else if (status === "warning" || status === "pending") {
      toast.warning(readableMessage);
    } else {
      toast.error(readableMessage);
    }

    if (integration === "linkedin_pages" && params.get("select_pages") === "1") {
      setTimeout(() => {
        openLinkedInPagePicker();
      }, 250);
    }

    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const filteredWorkflows = useMemo(() => {
    if (workflowTab === "all") return workflows;
    return workflows.filter((workflow) => workflowStatus(workflow) === workflowTab);
  }, [workflows, workflowTab]);

  const filteredApprovals = useMemo(() => {
    if (approvalTab === "all") return approvals;
    return approvals.filter((item) => normalizeStatus(item.status) === approvalTab);
  }, [approvals, approvalTab]);

  const filteredTasks = useMemo(() => {
    return orderActionTasksForDisplay(tasks, taskTab);
  }, [tasks, taskTab]);

  const projectById = useMemo(() => {
    const map = new Map();
    projects.forEach((project) => {
      if (project?.project_id) map.set(project.project_id, project);
    });
    return map;
  }, [projects]);

  const projectByWorkflowId = useMemo(() => {
    const map = new Map();

    workflows.forEach((workflow) => {
      const id = workflowId(workflow);
      if (!id) return;

      const projectId = itemProjectId(workflow);
      if (projectId && projectById.has(projectId)) {
        map.set(id, projectById.get(projectId));
        return;
      }

      const fallbackName = itemProjectName(workflow);
      const fallbackStatus = itemProjectStatus(workflow);

      if (projectId || fallbackName || fallbackStatus) {
        map.set(id, {
          project_id: projectId,
          name: fallbackName || projectId || "Project",
          status: fallbackStatus || "active",
        });
      }
    });

    return map;
  }, [workflows, projectById]);

  const activeProjects = useMemo(
    () => projects.filter((project) => !isArchivedProject(project)),
    [projects]
  );

  const taskById = useMemo(() => {
    const map = new Map();

    tasks.forEach((task) => {
      const id = actionTaskId(task);
      if (id) map.set(id, task);
    });

    return map;
  }, [tasks]);

  const canCreateWorkflow = activeProjects.length > 0;
  const showNoActiveProjectWarning = !loading && !canCreateWorkflow;

  const projectForItem = (item, seenTaskIds = new Set()) => {
    const projectId = itemProjectId(item);

    if (projectId && projectById.has(projectId)) {
      return projectById.get(projectId);
    }

    const itemWorkflowId = workflowId(item);
    if (itemWorkflowId && projectByWorkflowId.has(itemWorkflowId)) {
      return projectByWorkflowId.get(itemWorkflowId);
    }

    const sourceId = sourceTaskIdForItem(item);
    if (sourceId && !seenTaskIds.has(sourceId) && taskById.has(sourceId)) {
      seenTaskIds.add(sourceId);
      const sourceProject = projectForItem(taskById.get(sourceId), seenTaskIds);

      if (sourceProject) {
        return sourceProject;
      }
    }

    const fallbackStatus = itemProjectStatus(item);
    const fallbackName = itemProjectName(item);
    const usableFallbackName = isGenericProjectLabel(fallbackName) ? "" : fallbackName;

    if (projectId || usableFallbackName || fallbackStatus) {
      return {
        project_id: projectId,
        name: usableFallbackName || "",
        status: fallbackStatus || "active",
      };
    }

    return null;
  };

  const projectArchivedForItem = (item) => {
    const project = projectForItem(item);
    if (project) return isArchivedProject(project);
    return ["archived", "deleted"].includes(itemProjectStatus(item));
  };

  const requireActiveProjectForItem = (item, action = "continue") => {
    if (!projectArchivedForItem(item)) return true;

    const project = projectForItem(item);
    toast.error(`${projectDisplayName(project, "This project")} is archived. Restore it before you ${action}.`);
    return false;
  };

  const activeWorkflowCount = workflows.filter(
    (workflow) => workflowStatus(workflow) === "active"
  ).length;
  const pendingCount = approvals.filter((item) => normalizeStatus(item.status) === "pending").length;
  const readyCount = tasks.filter((item) => normalizeStatus(item.status) === "ready").length;
  const scheduledCount = tasks.filter((item) => normalizeStatus(item.status) === "scheduled").length;

  const linkedinProfileAccounts = connectedAccounts.filter(isLinkedInProfileAccount);
  const linkedinPageAccounts = connectedAccounts.filter(isLinkedInPageAccount);
  const linkedinAccounts = [...linkedinProfileAccounts, ...linkedinPageAccounts];
  const wordpressAccounts = connectedAccounts.filter((account) => account.platform === "wordpress");
  const primaryLinkedInAccount = linkedinAccounts[0] || null;
  const primaryLinkedInPageAccount = linkedinPageAccounts[0] || null;
  const primaryWordPressAccount = wordpressAccounts[0] || null;
  const selectedLinkedInAccount =
    linkedinAccounts.find((account) => accountId(account) === publishingAccountIds.linkedin) || primaryLinkedInAccount;
  const selectedWordPressAccount =
    wordpressAccounts.find((account) => accountId(account) === publishingAccountIds.wordpress) || primaryWordPressAccount;

  const adminUser = isPlatformAdminUser(user);

  const publishingSlotLimit = Math.max(
    3,
    Number(
      usage?.publishing_account_slots?.limit ||
        usage?.publishing_account_slots?.linkedin ||
        usage?.account_slots?.publishing ||
        3
    )
  );

  const linkedinPageSlotLimit = adminUser ? null : publishingSlotLimit;
  const canConnectLinkedIn = linkedinProfileAccounts.length < publishingSlotLimit;
  const canConnectLinkedInPages = adminUser || linkedinPageAccounts.length < publishingSlotLimit;
  const canConnectWordPress = wordpressAccounts.length < publishingSlotLimit;

  const setPublishingAccountForType = (type, id) => {
    setPublishingAccountIds((prev) => ({ ...prev, [type]: id }));
  };

  const runAction = async (id, action, request, successMessage = "Workflow updated.") => {
    setWorkingId(`${action}:${id}`);

    try {
      await request();
      toast.success(successMessage);
      await load();
      if (refreshUsage) await refreshUsage();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setWorkingId(null);
    }
  };

  const updateTaskMediaState = (taskId, media) => {
    const applyMedia = (item) => mergeTaskMedia(item, media);

    setTasks((prev) =>
      prev.map((task) => (actionTaskId(task) === taskId ? applyMedia(task) : task))
    );

    setPublishingPreview((prev) => {
      if (!prev?.item || actionTaskId(prev.item) !== taskId) return prev;
      return { ...prev, item: applyMedia(prev.item) };
    });

    setScheduleModal((prev) => {
      if (!prev?.task || actionTaskId(prev.task) !== taskId) return prev;
      return { ...prev, task: applyMedia(prev.task) };
    });

    setPreview((prev) => {
      if (!prev || actionTaskId(prev) !== taskId) return prev;
      return applyMedia(prev);
    });
  };

  const uploadTaskMedia = async (item, file) => {
    const taskId = actionTaskId(item);

    if (!taskId || !file) return;
    if (!requireActiveProjectForItem(item, "change the image for this task")) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, PNG and WebP images are allowed.");
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is too large. Maximum size is 8 MB.");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    setWorkingId(`media-upload:${taskId}`);

    try {
      const { data } = await axios.post(`${API}/action-tasks/${encodeURIComponent(taskId)}/media`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      updateTaskMediaState(taskId, data?.media || null);
      toast.success("Image attached.");
    } catch (err) {
      const data = err?.response?.data || {};
      const policyMessage =
        data.policy_warning_message ||
        data.warning_message ||
        data.error ||
        getApiErrorMessage(err, "Could not upload image.");

      toast.error(policyMessage, {
        duration: data.attempt_number && data.attempt_number >= 3 ? 14000 : 9000,
      });
    } finally {
      setWorkingId(null);
    }
  };

  const removeTaskMedia = async (item) => {
    const taskId = actionTaskId(item);

    if (!taskId) return;
    if (!requireActiveProjectForItem(item, "remove the image from this task")) return;

    setWorkingId(`media-remove:${taskId}`);

    try {
      await axios.delete(`${API}/action-tasks/${encodeURIComponent(taskId)}/media`);
      updateTaskMediaState(taskId, null);
      toast.success("Image removed.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not remove image."));
    } finally {
      setWorkingId(null);
    }
  };

  const createWorkflow = async (form) => {
    const selectedProject = projectById.get(form.project_id);

    if (!selectedProject || isArchivedProject(selectedProject)) {
      toast.error("Choose an active project before creating a workflow.");
      return;
    }

    setCreatingWorkflow(true);

    try {
      await axios.post(`${API}/workflows`, buildWorkflowPayload({ ...form, project_name: selectedProject.name }, true));
      toast.success("Workflow created.");
      setCreateModalOpen(false);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not create workflow."));
    } finally {
      setCreatingWorkflow(false);
    }
  };

  const openEditModal = (workflow) => {
    setEditModal({ open: true, workflow });
  };

  const updateWorkflow = async (form) => {
    const id = workflowId(editModal.workflow);
    if (!id) return;

    if (!requireActiveProjectForItem(editModal.workflow, "edit this workflow")) return;

    const selectedProject = projectById.get(form.project_id);
    if (!selectedProject || isArchivedProject(selectedProject)) {
      toast.error("Choose an active project before saving this workflow.");
      return;
    }

    setSavingWorkflow(true);

    try {
      const enabled = workflowStatus(editModal.workflow) === "active";
      await axios.patch(`${API}/workflows/${id}`, buildWorkflowPayload({ ...form, project_name: selectedProject.name }, enabled));
      toast.success("Workflow updated.");
      setEditModal({ open: false, workflow: null });
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not update workflow."));
    } finally {
      setSavingWorkflow(false);
    }
  };

  const runWorkflowNow = (workflow) => {
    const id = workflowId(workflow);
    if (!id) return;

    if (!requireActiveProjectForItem(workflow, "run this workflow")) return;

    if (String(workingId || "").startsWith("run-workflow:")) {
      toast.warning("Another workflow is already running. Please wait for it to finish first.");
      return;
    }

    runAction(
      id,
      "run-workflow",
      () => axios.post(`${API}/workflows/${id}/run`),
      "Workflow run started."
    );
  };

  const toggleWorkflow = (workflow) => {
    const id = workflowId(workflow);
    if (!id) return;

    if (!requireActiveProjectForItem(workflow, "change this workflow")) return;

    const nextStatus = workflowStatus(workflow) === "active" ? "paused" : "active";
    const preservedForm = workflowToForm(workflow);

    runAction(
      id,
      "toggle-workflow",
      () => axios.patch(`${API}/workflows/${id}`, buildWorkflowPayload(preservedForm, nextStatus === "active")),
      nextStatus === "active" ? "Workflow resumed." : "Workflow paused."
    );
  };

  const openRunsModal = async (workflow) => {
    const id = workflowId(workflow);
    if (!id) return;

    setRunsModal({ open: true, workflow, runs: [], loading: true });

    try {
      const { data } = await axios.get(`${API}/workflows/${id}/runs`, {
        params: { limit: 30 },
      });
      const latestRuns = readItems(data).slice(0, 30);
      setRunsModal({ open: true, workflow, runs: latestRuns, loading: false });
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not load workflow runs."));
      setRunsModal({ open: true, workflow, runs: [], loading: false });
    }
  };

  const approve = (approval) => {
    const approvalId = approval?.approval_id || approval?.id || approval;
    if (!approvalId) return;
    if (!requireActiveProjectForItem(approval, "approve this draft")) return;

    runAction(
      approvalId,
      "approve",
      () => axios.post(`${API}/approvals/${approvalId}/approve`),
      "Draft approved and action task created."
    );
  };

  const openRejectModal = (approval) => {
    if (!requireActiveProjectForItem(approval, "reject this draft")) return;

    setRejectModal({
      open: true,
      approvalId: approval.approval_id || approval.id,
      title: approval.title,
      feedback: "",
    });
  };

  const submitReject = async () => {
    if (!rejectModal.approvalId) return;

    await runAction(
      rejectModal.approvalId,
      "reject",
      () =>
        axios.post(`${API}/approvals/${rejectModal.approvalId}/reject`, {
          feedback: rejectModal.feedback,
        }),
      "Draft rejected."
    );

    setRejectModal({ open: false, approvalId: null, title: "", feedback: "" });
  };

  const openScheduleModal = (task) => {
    if (!requireActiveProjectForItem(task, "schedule this task")) return;

    const metadata = safeJsonObject(task?.metadata);
    const schedule = safeJsonObject(metadata?.schedule);
    const savedAccountId = String(task?.account_id || schedule?.account_id || metadata?.account_id || "").trim();
    const savedAccount = connectedAccounts.find((account) => accountId(account) === savedAccountId) || null;
    const savedPlatform = String(
      task?.publish_platform ||
        schedule?.platform ||
        metadata?.publish_platform ||
        metadata?.platform ||
        ""
    ).toLowerCase();

    const defaultDestination =
      savedPlatform === "wordpress" || savedAccount?.platform === "wordpress"
        ? "wordpress"
        : "linkedin";

    const defaultAccount =
      savedAccount ||
      (defaultDestination === "linkedin"
        ? selectedLinkedInAccount || linkedinAccounts[0] || null
        : selectedWordPressAccount || wordpressAccounts[0] || null);

    const rawSavedIndex =
      task?.selected_post_index ??
      metadata?.selected_post_index ??
      schedule?.selected_post_index ??
      metadata?.selected_post?.index ??
      schedule?.selected_post?.index ??
      null;

    const rawSavedNumber =
      task?.selected_post_number ??
      metadata?.selected_post_number ??
      schedule?.selected_post_number ??
      metadata?.selected_post?.number ??
      schedule?.selected_post?.number ??
      null;

    const savedIndex = rawSavedIndex !== null && rawSavedIndex !== undefined && rawSavedIndex !== ""
      ? Number(rawSavedIndex)
      : (rawSavedNumber !== null && rawSavedNumber !== undefined && rawSavedNumber !== "" ? Number(rawSavedNumber) - 1 : 0);

    setScheduleModal({
      open: true,
      taskId: task.task_id || task.id,
      title: task.title,
      scheduledFor: utcToLocalInput(task.scheduled_for),
      task,
      selectedPartIndex: Number.isFinite(savedIndex) && savedIndex >= 0 ? savedIndex : 0,
      destination: defaultDestination,
      accountId: accountId(defaultAccount) || "",
      ctaUrl: String(schedule?.cta_url || metadata?.cta_url || schedule?.selected_post?.cta_url || metadata?.selected_post?.cta_url || ""),
    });
  };

  const submitSchedule = async () => {
    if (!scheduleModal.taskId) return;

    if (!requireActiveProjectForItem(scheduleModal.task, "schedule this task")) return;

    if (!scheduleModal.scheduledFor) {
      toast.error("Please choose a date and time.");
      return;
    }

    const destination = scheduleModal.destination || "linkedin";
    const destinationLabel = destination === "wordpress" ? "WordPress" : "LinkedIn";
    const destinationAccounts = destination === "wordpress" ? wordpressAccounts : linkedinAccounts;
    const selectedAccount =
      destinationAccounts.find((entry) => accountId(entry) === scheduleModal.accountId) ||
      destinationAccounts[0] ||
      null;

    if (!selectedAccount) {
      toast.error(`Connect ${destinationLabel} first before scheduling.`);
      return;
    }

    const utcFormatted = localInputToUtcMysql(scheduleModal.scheduledFor);

    const scheduleParts = publishingPartsForItem(scheduleModal.task, destination);
    const selectedPart = scheduleParts[scheduleModal.selectedPartIndex] || scheduleParts[0] || null;
    const cleanTitle = destination === "wordpress"
      ? titleFromSelectedPart(
          selectedPart,
          scheduleModal.task,
          publicPublishingTitle(scheduleModal.task, "Scheduled WordPress draft")
        )
      : publicPublishingTitle(scheduleModal.task, "Scheduled LinkedIn post");

    const selectedPostNumber = selectedPart ? scheduleModal.selectedPartIndex + 1 : null;

    const schedulePayload = {
      scheduled_for: utcFormatted,
      destination,
      platform: destination,
      publish_platform: destination,
      publishing_platform: destination,
      schedule_destination: destination,
      account_id: accountId(selectedAccount),
      connected_account_id: accountId(selectedAccount),
      publishing_account_id: accountId(selectedAccount),
      account_name: accountLabel(selectedAccount),
      wordpress_status: destination === "wordpress" ? "draft" : undefined,
      cta_url: scheduleModal.ctaUrl || undefined,
      link_url: scheduleModal.ctaUrl || undefined,
      media: taskMedia(scheduleModal.task) || undefined,
      image: taskMedia(scheduleModal.task) || undefined,
    };

    if (selectedPart) {
      schedulePayload.selected_post_index = scheduleModal.selectedPartIndex;
      schedulePayload.selected_post_number = selectedPostNumber;
      schedulePayload.selected_post_text = selectedPart.text;
      schedulePayload.selected_post_title = cleanTitle;
      schedulePayload.post_index = scheduleModal.selectedPartIndex;
      schedulePayload.post_number = selectedPostNumber;
      schedulePayload.post_text = selectedPart.text;
      schedulePayload.post_title = cleanTitle;
      schedulePayload.text = selectedPart.text;
      schedulePayload.selected_post = {
        label: selectedPart.title,
        index: scheduleModal.selectedPartIndex,
        number: selectedPostNumber,
        content: selectedPart.text,
        text: selectedPart.text,
        title: cleanTitle,
        destination,
        platform: destination,
        account_id: accountId(selectedAccount),
        cta_url: scheduleModal.ctaUrl || undefined,
        media: taskMedia(scheduleModal.task) || undefined,
        image: taskMedia(scheduleModal.task) || undefined,
      };
      schedulePayload.split_post = scheduleParts.length > 1;
      schedulePayload.selected_part_label = selectedPart.title;
      schedulePayload.content = selectedPart.text;
      schedulePayload.title = cleanTitle;
    }

    await runAction(
      scheduleModal.taskId,
      "schedule",
      () => axios.post(`${API}/action-tasks/${scheduleModal.taskId}/schedule`, schedulePayload),
      selectedPart
        ? `${selectedPart.title} scheduled for ${destinationLabel}.`
        : `Task scheduled for ${destinationLabel}.`
    );

    closeScheduleModal();
  };

  const publishTask = (task) => {
    const taskId = task?.task_id || task?.id || task;
    if (!taskId) return;
    if (!requireActiveProjectForItem(task, "publish this task")) return;

    runAction(
      taskId,
      "publish",
      () => axios.post(`${API}/action-tasks/${taskId}/publish`),
      "Task marked as published."
    );
  };

  const completeTask = (task) => {
    const taskId = task?.task_id || task?.id || task;
    if (!taskId) return;
    if (!requireActiveProjectForItem(task, "complete this task")) return;

    runAction(
      taskId,
      "complete",
      () => axios.post(`${API}/action-tasks/${taskId}/complete`),
      "Task completed."
    );
  };

  const openArchiveModal = (task) => {
    setArchiveModal({
      open: true,
      taskId: task.task_id || task.id,
      title: task.title,
    });
  };

  const submitArchive = async () => {
    if (!archiveModal.taskId) return;

    await runAction(
      archiveModal.taskId,
      "archive",
      () => axios.post(`${API}/action-tasks/${archiveModal.taskId}/archive`),
      "Task archived."
    );

    setArchiveModal({ open: false, taskId: null, title: "" });
  };

  const openLinkedInPagePicker = async () => {
    setLinkedinPagePicker((prev) => ({
      ...prev,
      open: true,
      loading: true,
    }));

    try {
      const { data } = await axios.get(`${API}/integrations/linkedin_pages/available`);
      const pages = readItems(data);
      const selectedIds = pages
        .filter((page) => page.selected || ["connected", "active"].includes(normalizeStatus(page.status)))
        .map((page) => accountId(page))
        .filter(Boolean);

      setLinkedinPagePicker({
        open: true,
        loading: false,
        saving: false,
        pages,
        selectedIds,
        limit: data?.limit ?? 3,
        unlimited: Boolean(data?.unlimited),
        connectedCount: Number(data?.connected_count || 0),
        remaining: data?.remaining ?? null,
      });

      if (pages.length === 0) {
        toast.warning(data?.message || "No LinkedIn Pages are waiting for selection. Click Connect LinkedIn Page first.");
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not load LinkedIn Pages for selection."));
      setLinkedinPagePicker((prev) => ({ ...prev, loading: false }));
    }
  };

  const toggleLinkedInPageSelection = (pageId) => {
    const id = String(pageId || "").trim();
    if (!id) return;

    setLinkedinPagePicker((prev) => {
      const selected = prev.selectedIds.includes(id);
      const nextSelected = selected
        ? prev.selectedIds.filter((item) => item !== id)
        : [...prev.selectedIds, id];

      if (!selected && !prev.unlimited && nextSelected.length > Number(prev.limit || 0)) {
        toast.error(`You can select up to ${prev.limit} LinkedIn Pages on your current plan.`);
        return prev;
      }

      return {
        ...prev,
        selectedIds: nextSelected,
      };
    });
  };

  const saveLinkedInPageSelection = async () => {
    setLinkedinPagePicker((prev) => ({ ...prev, saving: true }));

    try {
      await axios.post(`${API}/integrations/linkedin_pages/select`, {
        account_ids: linkedinPagePicker.selectedIds,
      });

      toast.success("LinkedIn Page selection saved.");
      setLinkedinPagePicker((prev) => ({ ...prev, open: false, saving: false }));
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not save LinkedIn Page selection."));
      setLinkedinPagePicker((prev) => ({ ...prev, saving: false }));
    }
  };

  const connectLinkedIn = async () => {
    setConnectingId("linkedin");

    try {
      const { data } = await axios.post(`${API}/integrations/linkedin/connect`);
      if (!data?.auth_url) throw new Error("Missing LinkedIn authorization URL.");
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not start LinkedIn connection."));
      setConnectingId(null);
    }
  };

  const connectLinkedInPages = async () => {
    setConnectingId("linkedin_pages");

    try {
      const { data } = await axios.post(`${API}/integrations/linkedin_pages/connect`);
      if (!data?.auth_url) throw new Error("Missing LinkedIn Pages authorization URL.");
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not start LinkedIn Pages connection."));
      setConnectingId(null);
    }
  };

  const connectWordPress = async (form) => {
    setConnectingId("wordpress");

    try {
      await axios.post(`${API}/integrations/wordpress/connect`, form);
      toast.success("WordPress connected.");
      setWordpressModalOpen(false);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not connect WordPress."));
    } finally {
      setConnectingId(null);
    }
  };

  const openDisconnectModal = (account) => {
    if (!accountId(account)) return;
    setDisconnectModal({ open: true, account });
  };

  const disconnectPublishingAccount = async () => {
    const account = disconnectModal.account;
    const id = accountId(account);
    if (!id) return;

    const label = accountLabel(account);
    setWorkingId(`disconnect:${id}`);

    try {
      await axios.delete(`${API}/integrations/accounts/${encodeURIComponent(id)}`);
      toast.success(`${label} disconnected.`);
      setPublishingAccountIds((prev) => {
        const next = { ...prev };
        if (next.linkedin === id) next.linkedin = "";
        if (next.linkedin_pages === id) next.linkedin_pages = "";
        if (next.wordpress === id) next.wordpress = "";
        return next;
      });
      setDisconnectModal({ open: false, account: null });
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not disconnect account. The backend returned an error, so this account is still connected."));
    } finally {
      setWorkingId(null);
    }
  };

  const publishToLinkedIn = async (item, part = null, accountOverride = null, options = {}) => {
    const id = item.task_id || item.approval_id || item.id || "linkedin";
    const text = part?.text || itemBody(item);
    const account = accountOverride || selectedLinkedInAccount;
    const ctaUrl = String(options?.ctaUrl || "").trim();

    if (!text.trim()) {
      toast.error("There is no content to publish.");
      return;
    }

    if (!account) {
      toast.error("Connect LinkedIn first.");
      return;
    }

    if (!requireActiveProjectForItem(item, "publish from this task")) return;

    setWorkingId(`linkedin:${id}`);

    try {
      await axios.post(`${API}/integrations/linkedin/publish`, {
        account_id: accountId(account),
        text,
        cta_url: ctaUrl || undefined,
        link_url: ctaUrl || undefined,
        media: taskMedia(item) || undefined,
        image: taskMedia(item) || undefined,
        image_url: mediaUrl(taskMedia(item)) || undefined,
        visibility: "PUBLIC",
      });
      toast.success("Published to LinkedIn.");
      setPublishingPreview(null);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not publish to LinkedIn."));
    } finally {
      setWorkingId(null);
    }
  };

  const createWordPressDraft = async (item, part = null, accountOverride = null, options = {}) => {
    const id = item.task_id || item.approval_id || item.id || "wordpress";
    const parts = publishingPartsForItem(item, "wordpress");
    const selectedPart = part || parts[0] || null;
    const title = titleFromSelectedPart(selectedPart, item, publicPublishingTitle(item, "Ordeneli marketing draft"));
    const content = selectedPart?.text || itemBody(item);
    const account = accountOverride || selectedWordPressAccount;
    const ctaUrl = String(options?.ctaUrl || "").trim();

    if (!content.trim()) {
      toast.error("There is no content to send to WordPress.");
      return;
    }

    if (!account) {
      toast.error("Connect WordPress first.");
      return;
    }

    if (!requireActiveProjectForItem(item, "create a WordPress draft from this task")) return;

    setWorkingId(`wordpress:${id}`);

    try {
      const { data } = await axios.post(`${API}/integrations/wordpress/publish`, {
        account_id: accountId(account),
        title,
        content,
        cta_url: ctaUrl || undefined,
        link_url: ctaUrl || undefined,
        cta_label: "Visit website",
        media: taskMedia(item) || undefined,
        image: taskMedia(item) || undefined,
        image_url: mediaUrl(taskMedia(item)) || undefined,
        status: "draft",
      });
      toast.success("WordPress draft created.");
      setPublishingPreview(null);
      if (data?.post_link) {
        window.open(data.post_link, "_blank", "noopener,noreferrer");
      }
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not create WordPress draft."));
    } finally {
      setWorkingId(null);
    }
  };

  const exportNewsletter = async (item) => {
    const id = item.task_id || item.approval_id || item.id || "newsletter";
    if (!requireActiveProjectForItem(item, "export this newsletter")) return;

    const title = itemTitle(item, "Ordeneli newsletter");
    const content = itemBody(item);

    if (!content.trim()) {
      toast.error("There is no content to export.");
      return;
    }

    setWorkingId(`newsletter:${id}`);

    try {
      const { data } = await axios.post(`${API}/integrations/newsletter/export`, {
        title,
        brand: "Ordeneli",
        content,
        cta_text: "Start growing with AI",
        cta_url: "https://ordeneli.com",
        footer_note: "You are receiving this because you use Ordeneli Pro.",
        media: taskMedia(item) || undefined,
        image_url: mediaUrl(taskMedia(item)) || undefined,
      });
      setNewsletterExport({ ...data, media: taskMedia(item) || null });
      toast.success("Newsletter export ready.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not export newsletter."));
    } finally {
      setWorkingId(null);
    }
  };

  const downloadPlan = (item) => {
    const content = itemBody(item);
    const title = itemTitle(item, "Ordeneli plan");

    if (!content.trim()) {
      toast.error("There is no plan content to download.");
      return;
    }

    const safeName = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "ordeneli-plan";

    const blob = new Blob([`${title}

${content}`], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeName}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success("Plan downloaded.");
  };

  const showLocalPlanActionPreview = (item, actionKey) => {
  const title = itemTitle(item, "Ordeneli plan");
  const content = itemBody(item);
  const toolKey = item.tool_key || item.tool || "plan";

  if (!String(content || "").trim()) {
    toast.error("There is no plan content to use.");
    return false;
  }

  const planSubject =
    title
      .replace(/^Ready\s+for\s+/i, "")
      .replace(/\s+-\s+Local Draft\s+-\s+/i, " - ")
      .replace(/\s{2,}/g, " ")
      .trim() || "this marketing plan";

  const businessName =
    String(title || "")
      .split(" - ")
      .map((part) => part.trim())
      .filter(Boolean)
      .pop() || "the business";

  const cleanLines = String(content)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const actionLines = cleanLines.filter((line) =>
    /^(day\s*\d+|week\s*\d+|\d+[.)]|[-*•]\s+|add |create |build |publish |submit |set up|setup |upload |review |optimise |optimize |write |track |enable |prepare |update |launch |check |audit |monitor |implement |generate |reply |research |draft |schedule |contact |list )/i.test(
      line
    )
  );

  const makeChecklist = () => {
    const selected = actionLines.length ? actionLines : cleanLines.slice(0, 40);

    return selected
      .slice(0, 70)
      .map((line) => `☐ ${line.replace(/^[-*•]\s+/, "")}`)
      .join("\n");
  };

  const makeGbpPosts = () => `Post 1 — Update
We are making it easier for customers to understand what ${businessName} offers, how it helps, and what they should do next.

If you are comparing options or looking for a clearer way to get started, the next step is simple: check the service details, ask questions, and choose the support that fits your needs.

CTA:
Learn more / Book now / Contact us

---

Post 2 — Offer
Need a simpler way to get started with ${planSubject}?

${businessName} can help customers understand the service, compare options, and take the next step with confidence.

CTA:
Request a demo / Get started / Contact us

---

Post 3 — Educational
Before choosing a provider, customers should look for clear information, helpful answers, reliable support, and proof that the business can deliver.

That is why we are improving the way ${businessName} explains services, answers questions, and helps customers take action.

CTA:
Read more / Contact us / Learn more`;

  const makeLinkedInPost = () => `Post 1 — Awareness
Still trying to turn a marketing plan into real enquiries?

For ${businessName}, the priority is not just creating more content. It is making the offer clearer, building trust, and helping the right people take action.

A strong plan should lead to:
- clearer service pages
- helpful posts
- stronger local visibility
- better reviews and trust signals
- tracking for calls, clicks, and enquiries

The goal is simple: make it easier for customers to understand the value and take the next step.

CTA:
Book a demo / Contact us / Learn more

---

Post 2 — Educational
Local visibility is not only about being found.

Customers also need to quickly understand:
- what you offer
- who it is for
- why they should trust you
- what happens next
- how to contact or book

That is why ${businessName} needs useful content, strong local profiles, clear landing pages, and simple calls to action.

Better visibility works best when it also creates trust.

CTA:
Read more / Contact us / Get started

---

Post 3 — Action
A marketing plan becomes valuable when it turns into weekly action.

For ${businessName}, the next steps are:
1. improve the main service page
2. publish helpful content
3. update local profiles
4. collect and reply to reviews
5. measure calls, clicks, bookings, and leads

Small improvements done consistently can create a much stronger customer journey.

CTA:
Request a demo / Contact us / Learn more`;

  const makeWordPressPost = () => `# How ${businessName} can turn local visibility into more enquiries

Customers rarely make a decision from one piece of information. They usually compare the service, check whether the business looks trustworthy, read reviews, look for clear answers, and then decide whether to make contact.

That is why a marketing plan should not sit in a document. It should become useful website content, clear local profile updates, practical social posts, stronger service pages, and measurable actions.

For ${businessName}, the opportunity is to make the customer journey easier from the first search to the first enquiry.

## Start with the customer problem

The first step is to explain the problem in simple language. Customers need to know that the business understands what they are trying to solve and why it matters.

A strong page or post should quickly answer:

- what the service is
- who it helps
- what result the customer can expect
- how the process works
- how to contact the business

When this information is missing or unclear, customers may leave before making an enquiry.

## Make the offer easy to understand

A clear offer helps customers decide faster. Instead of only listing features, the content should explain the benefit behind each feature.

For example, customers want to know whether the service saves time, reduces stress, improves compliance, makes reporting easier, or helps them avoid mistakes.

This makes the message more useful and more persuasive.

## Build trust before the enquiry

Trust signals are important for local and service-based businesses. Useful trust signals include reviews, FAQs, case studies, photos, clear contact details, service areas, and proof that the business is active.

For ${businessName}, these trust signals should appear across the website, Google Business Profile, social posts, and any important directory listings.

## Turn the plan into content

A plan becomes useful when it becomes published content. The next content assets should include a focused service page, helpful blog articles, Google Business Profile posts, LinkedIn updates, and answers to common customer questions.

Each piece of content should guide the customer towards one simple next step.

## Track what creates enquiries

Marketing should be measured. Calls, website clicks, demo bookings, form submissions, and ranking improvements should be tracked so the business can see what is working.

This makes it easier to improve the content, adjust the offer, and focus on the channels that bring real enquiries.

## Conclusion

The best marketing plan is the one that turns into action. By improving the website, strengthening local profiles, publishing helpful content, and tracking enquiries, ${businessName} can create a clearer path from customer interest to customer action.

CTA:
Contact us, request a demo, or learn more about how ${businessName} can help.`;

  const makeLandingPage = () => `# ${businessName}: clear local support for customers ready to take action

## Hero section

Customers should not have to guess what you offer, who it is for, or how to get started.

${businessName} helps customers understand the service clearly, compare their options, and take the next step with confidence.

Primary CTA:
Book a demo / Contact us / Get started

Secondary CTA:
Learn how it works

## What we help with

${businessName} gives customers a clearer way to understand ${planSubject}. The service is designed to make the process easier, reduce confusion, and provide practical support from first enquiry to next step.

Whether someone is researching options, comparing providers, or ready to speak with the business, the page should give them the answers they need.

## Who this is for

This service is ideal for customers who want:

- clear information before making contact
- a reliable provider they can trust
- simple next steps
- helpful support
- a practical solution, not confusing jargon

## Key benefits

### Clearer information

Customers can quickly understand the offer, the value, and the process.

### More trust

Reviews, FAQs, proof, and useful content help customers feel more confident.

### Easier enquiries

Strong calls to action make it simple to request a demo, ask a question, or get started.

### Better local visibility

A focused page supports local search, Google Business Profile activity, and customer discovery.

## How it works

1. Review the customer problem and the main service need.
2. Explain the most relevant solution clearly.
3. Answer common questions before the customer asks.
4. Give the customer a simple way to enquire.
5. Track calls, clicks, bookings, and form submissions.

## Frequently asked questions

### What does this service include?

It includes clear information, practical guidance, and support based on what the customer needs.

### How quickly can I get started?

Customers can usually start by making an enquiry, requesting a demo, or contacting the business directly.

### Is support included?

Support should be clearly explained on the page, including how customers can ask questions or request help.

### What areas do you serve?

The page should include the main service area and any important local locations.

### How do I request a quote or demo?

Use the contact form, booking link, phone number, or main call-to-action button.

## Final call to action

Ready to take the next step?

Contact ${businessName} today to learn more, ask a question, or book a demo.`;

  const makeBlogPost = () => `# How to turn a marketing plan into real enquiries

A marketing plan is only useful when it becomes action. Many businesses create a plan, save it, and then struggle to turn it into content, website improvements, local visibility, and measurable results.

For ${businessName}, the goal should be simple: make it easier for customers to understand the offer, trust the business, and take the next step.

## Start with clarity

Customers need clear answers before they enquire. They want to know what the service includes, who it is for, how it works, and what result they can expect.

If this information is difficult to find, the customer may move on to another provider. That is why the first step is to improve the main service message and make the offer easy to understand.

## Build useful website content

The website should work like a sales assistant. It should explain the problem, present the solution, answer common questions, show trust signals, and guide visitors towards a clear call to action.

Useful content can include service pages, FAQs, case studies, comparison-style articles, and local landing pages.

## Strengthen local visibility

Local visibility is about more than rankings. It includes Google Business Profile updates, consistent business information, reviews, photos, posts, and links from relevant directories.

When these elements are consistent, customers are more likely to trust the business before they even visit the website.

## Publish consistently

A good plan should become a repeatable content workflow. Blog posts, LinkedIn updates, Google Business Profile posts, and landing page improvements should all support the same message.

Consistency helps the business stay visible and gives customers more reasons to engage.

## Track the results

The final step is measurement. Calls, website clicks, form submissions, bookings, and search queries should be reviewed regularly.

This shows which actions are creating enquiries and which areas need improvement.

## Conclusion

Turning a plan into results means publishing useful content, improving the customer journey, strengthening trust signals, and tracking what works.

For ${businessName}, the next step is to turn the plan into simple, consistent actions that help customers move from interest to enquiry.`;

  const outputs = {
    checklist: {
      title: `Tasks / checklist - ${title}`,
      content: makeChecklist(),
      toast: "Checklist preview created.",
    },
    "gbp-posts": {
      title: `GBP posts - ${title}`,
      content: makeGbpPosts(),
      toast: "GBP post preview created.",
      save_destination: "linkedin",
    },
    "linkedin-post": {
      title: `LinkedIn posts - ${title}`,
      content: makeLinkedInPost(),
      toast: "LinkedIn post preview created.",
      save_destination: "linkedin",
    },
    "wordpress-post": {
      title: `WordPress post - ${title}`,
      content: makeWordPressPost(),
      toast: "WordPress post preview created.",
      save_destination: "wordpress",
    },
    "wp-landing": {
      title: `WordPress landing page - ${title}`,
      content: makeLandingPage(),
      toast: "WordPress landing page preview created.",
      save_destination: "wordpress",
    },
    "blog-post": {
      title: `Blog post - ${title}`,
      content: makeBlogPost(),
      toast: "Blog post preview created.",
      save_destination: "wordpress",
    },
  };

  const output = outputs[actionKey];

  if (!output) {
    return false;
  }

  setPreview({
    id: `generated-${actionKey}-${item.task_id || item.id || Date.now()}`,
    task_id: item.task_id || item.id || null,
    source_task_id: item.task_id || item.id || null,
    source_title: itemTitle(item, "Source plan"),
    original_title: itemTitle(item, "Source plan"),
    project_id: itemProjectId(item) || undefined,
    project_name: itemProjectName(item) || undefined,
    project_status: itemProjectStatus(item) || "active",
    title: output.title,
    content: output.content,
    status: "generated",
    tool_key: toolKey,
    task_type: "plan_generated_output",
    generated_action_task: true,
    save_destination: output.save_destination || generatedPreviewSaveType({ title: output.title }),
    action_task_type: "publish_social_content",
    action_task_tool_key: "social",
    created_at: new Date().toISOString(),
  });

  toast.success(output.toast);
  return true;
};

const shouldUseLocalGeneratedFallback = (actionKey, generatedContent) => {
  const text = String(generatedContent || "").toLowerCase();

  if (!text.trim()) return true;
  if (actionKey === "checklist") return false;

  const hasSourcePlanDump = text.includes("source plan:") && text.length > 2500;
  const hasOnlySuggestedSections =
    text.includes("suggested sections:") &&
    !text.includes("## ") &&
    !text.includes("# ");

  return hasSourcePlanDump || hasOnlySuggestedSections;
};

const runPlanAction = async (item, endpoint, actionKey, successMessage) => {
  const id = item.task_id || item.id;

  if (!id) {
    toast.error("Missing task ID.");
    return;
  }

  if (!requireActiveProjectForItem(item, "generate from this plan")) return;

  setWorkingId(`${actionKey}:${id}`);

  try {
    const { data } = await axios.post(`${API}/action-tasks/${id}/${endpoint}`, {
      title: itemTitle(item, "Ordeneli plan"),
      content: itemBody(item),
      tool_key: item.tool_key || item.tool || null,
      project_id: itemProjectId(item) || undefined,
      project_name: itemProjectName(item) || undefined,
    });

    const generatedContent =
      data?.content ||
      data?.output ||
      data?.result ||
      data?.item?.content ||
      data?.task?.content ||
      "";

    if (shouldUseLocalGeneratedFallback(actionKey, generatedContent)) {
      showLocalPlanActionPreview(item, actionKey);
      return;
    }

    const generatedTitle =
      data?.title ||
      data?.item?.title ||
      data?.task?.title ||
      successMessage ||
      "Generated output";

    setPreview({
      id: `backend-${actionKey}-${id}`,
      task_id: id,
      source_task_id: id,
      source_title: itemTitle(item, "Source plan"),
      original_title: itemTitle(item, "Source plan"),
      project_id: itemProjectId(item) || undefined,
      project_name: itemProjectName(item) || undefined,
      project_status: itemProjectStatus(item) || "active",
      title: generatedTitle,
      content: generatedContent,
      status: "generated",
      tool_key: item.tool_key || item.tool || "plan",
      task_type: "backend_generated_output",
      generated_action_task: true,
      save_destination: ["wp-landing", "blog-post", "wordpress-post"].includes(actionKey)
        ? "wordpress"
        : "linkedin",
      action_task_type: "publish_social_content",
      action_task_tool_key: "social",
      created_at: new Date().toISOString(),
    });

    toast.success(successMessage);
    await load();
  } catch (err) {
    const usedLocalFallback = showLocalPlanActionPreview(item, actionKey);

    if (!usedLocalFallback) {
      toast.error(
        getApiErrorMessage(
          err,
          "This generator route is not connected in the backend yet."
        )
      );
    }
  } finally {
    setWorkingId(null);
  }
};

const saveGeneratedPreviewAsActionTask = async (item) => {
  if (!requireActiveProjectForItem(item, "save this generated content")) return;

  const payload = buildGeneratedActionTaskPayload(item);

  if (!String(payload.content || "").trim()) {
    toast.error("There is no generated content to save.");
    return;
  }

  setWorkingId(`save-action-task:${item.id || item.task_id || "preview"}`);

  try {
    const { data } = await axios.post(`${API}/action-tasks`, payload);
    const savedTask = data?.task || data?.item || data?.data || null;

    if (savedTask && typeof savedTask === "object") {
      setTasks((prev) => [savedTask, ...prev]);
    }

    toast.success("Saved as action task.");
    setTaskTab("ready");
    setPreview(null);
    await load();
  } catch (err) {
    toast.error(
      getApiErrorMessage(
        err,
        "Could not save this as a real action task. Add the backend POST /action-tasks route so it is stored in the database."
      )
    );
  } finally {
    setWorkingId(null);
  }
};

const confirmPublishingPreview = async (type, item, part, account, options = {}) => {
    if (!requireActiveProjectForItem(item, "publish this content")) return;

    if (type === "linkedin") {
      await publishToLinkedIn(item, part, account, options);
      return;
    }

    if (type === "wordpress") {
      await createWordPressDraft(item, part, account, options);
    }
  };

  if (!workflowUnlocked) {
    return (
      <div>
        <section className="border-b border-[#E5E7EB]">
          <div className="container-wide py-10 md:py-14">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">
              Workflow Center
            </p>

            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
              Automate your marketing workflow.
            </h1>

            <p className="mt-3 text-[#525252] max-w-2xl text-sm sm:text-base">
              Workflow automation is available on Pro. It turns AI drafts into approvals, scheduled tasks, and publish-ready actions.
            </p>
          </div>
        </section>

        <section className="container-wide py-10 md:py-14">
          <div className="border border-[#002FA7] bg-[#F5F5F5] p-8 md:p-10 max-w-3xl">
            <div className="h-11 w-11 bg-[#002FA7] text-white flex items-center justify-center">
              <WarningCircle size={24} weight="duotone" />
            </div>

            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mt-5">
              Pro feature
            </p>

            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-black tracking-tighter">
              Unlock approvals, action tasks and background automation.
            </h2>

            <p className="mt-4 text-[#525252] max-w-xl">
              Your current plan is <strong className="capitalize text-[#0A0A0A]">{currentPlan}</strong>. Upgrade to Pro to run recurring SEO, social, ads and marketing workflows in the background.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/pricing" className="btn-primary">
                View Pro plan
              </Link>

              <Link to="/dashboard" className="btn-secondary">
                Back to free tools
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b border-[#E5E7EB]">
        <div className="container-wide py-10 md:py-14 flex items-end justify-between gap-6 md:gap-8 flex-wrap">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">
              Workflow Center
            </p>

            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
              Automations, approvals and tasks.
            </h1>

            <p className="mt-3 text-[#525252] max-w-2xl text-sm sm:text-base">
              Create recurring AI workflows, review generated drafts, and move approved work into scheduled or published action tasks.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => canCreateWorkflow && setCreateModalOpen(true)}
              disabled={!canCreateWorkflow || loading}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="workflow-create-btn"
              title={!canCreateWorkflow ? "Add or restore an active project first." : "Create workflow"}
            >
              <Plus size={16} weight="bold" /> {loading ? "Loading projects..." : canCreateWorkflow ? "Create workflow" : "No active project"}
            </button>

            <button
              onClick={load}
              disabled={loading}
              className="btn-secondary"
              data-testid="workflow-refresh-btn"
            >
              <ArrowClockwise size={16} weight="bold" /> {loading ? "Refreshing..." : "Refresh"}
            </button>

            <span className="inline-flex items-center border border-[#E5E7EB] px-4 py-3 text-xs font-mono uppercase tracking-[0.14em] text-[#737373] bg-white">
              Cron runs every 10 minutes
            </span>
          </div>
        </div>
      </section>

      {showNoActiveProjectWarning && (
        <section className="container-wide pt-8">
          <div className="border border-[#FDE68A] bg-[#FFFBEB] p-5 text-sm text-[#92400E] flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <p className="font-semibold">No active project available.</p>
              <p className="mt-1">Create or restore a project in Settings before creating workflows. Archived projects remain visible, but they are view-only.</p>
            </div>
            <Link to="/settings?tab=plan" className="btn-secondary inline-flex self-start md:self-center">
              Manage projects
            </Link>
          </div>
        </section>
      )}

      <section className="container-wide py-8 md:py-10">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 sm:gap-4">
          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Active workflows</p>
            <p className="font-display text-3xl font-black mt-2">{activeWorkflowCount}</p>
          </div>

          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Pending approvals</p>
            <p className="font-display text-3xl font-black mt-2">{pendingCount}</p>
          </div>

          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Ready tasks</p>
            <p className="font-display text-3xl font-black mt-2">{readyCount}</p>
          </div>

          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Scheduled tasks</p>
            <p className="font-display text-3xl font-black mt-2">{scheduledCount}</p>
          </div>

          <div className="border border-[#E5E7EB] bg-white p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Runner status</p>

            <div className="mt-2 flex items-center gap-2">
              {normalizeStatus(health?.last_status) === "ok" ? (
                <CheckCircle size={20} weight="duotone" className="text-[#16A34A]" />
              ) : (
                <WarningCircle size={20} weight="duotone" className="text-[#B45309]" />
              )}

              <p className="font-display text-2xl font-black capitalize">
                {health?.last_status || "unknown"}
              </p>
            </div>

            <p className="mt-2 text-xs text-[#737373]">
              Last run: {formatDate(health?.last_finished_at || health?.updated_at)}
            </p>
          </div>
        </div>
      </section>

      <section className="container-wide pb-8 md:pb-10">
        <div className="border border-[#E5E7EB] bg-white">
          <div className="p-5 sm:p-6 border-b border-[#E5E7EB] flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">Connected publishing</p>
              <h2 className="font-display text-2xl font-black mt-1">Publishing accounts</h2>
              <p className="mt-2 text-xs text-[#737373] max-w-xl">
                Connect accounts once, then send approved tasks to LinkedIn, WordPress drafts, or newsletter exports.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={connectLinkedIn}
                disabled={connectingId === "linkedin" || !canConnectLinkedIn}
                className="btn-secondary !py-2 !px-3 text-xs disabled:opacity-70 disabled:cursor-default"
                data-testid="connect-linkedin-btn"
                title={canConnectLinkedIn ? (primaryLinkedInAccount ? "Connect another LinkedIn account" : "Connect LinkedIn") : "LinkedIn account limit reached"}
              >
                <PaperPlaneTilt size={14} weight="bold" /> {connectingId === "linkedin" ? "Connecting..." : !canConnectLinkedIn ? "LinkedIn limit reached" : primaryLinkedInAccount ? "Connect another LinkedIn" : "Connect LinkedIn"}
              </button>

              <button
                onClick={connectLinkedInPages}
                disabled={connectingId === "linkedin_pages" || !canConnectLinkedInPages}
                className="btn-secondary !py-2 !px-3 text-xs disabled:opacity-70 disabled:cursor-default"
                data-testid="connect-linkedin-pages-btn"
                title={canConnectLinkedInPages ? "Connect a LinkedIn company page after LinkedIn approves the Community Management API" : "LinkedIn Page account limit reached"}
              >
                <PaperPlaneTilt size={14} weight="bold" /> {connectingId === "linkedin_pages" ? "Connecting..." : !canConnectLinkedInPages ? "LinkedIn Page limit reached" : primaryLinkedInPageAccount ? "Connect another LinkedIn Page" : "Connect LinkedIn Page"}
              </button>

              <button
                onClick={() => setWordpressModalOpen(true)}
                disabled={connectingId === "wordpress" || !canConnectWordPress}
                className="btn-secondary !py-2 !px-3 text-xs disabled:opacity-70 disabled:cursor-default"
                data-testid="connect-wordpress-btn"
                title={canConnectWordPress ? (primaryWordPressAccount ? "Connect another WordPress site" : "Connect WordPress") : "WordPress account limit reached"}
              >
                <Plus size={14} weight="bold" /> {connectingId === "wordpress" ? "Connecting..." : !canConnectWordPress ? "WordPress limit reached" : primaryWordPressAccount ? "Connect another WordPress" : "Connect WordPress"}
              </button>
            </div>
          </div>

          <div className="p-5 sm:p-6 border-b border-[#E5E7EB] grid grid-cols-1 md:grid-cols-4 gap-3 bg-[#FAFAFA]">
            <div className="border border-[#E5E7EB] bg-white p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#0A66C2]">LinkedIn</p>
              <p className="font-semibold mt-1">Preview approved posts first, then publish to the connected profile.</p>
            </div>
            <div className="border border-[#E5E7EB] bg-white p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#0A66C2]">LinkedIn Pages</p>
              <p className="font-semibold mt-1">Connect company pages after LinkedIn approves your Community Management API access.</p>
            </div>
            <div className="border border-[#E5E7EB] bg-white p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#002FA7]">WordPress</p>
              <p className="font-semibold mt-1">Create safe blog drafts, then edit or publish inside WordPress.</p>
            </div>
            <div className="border border-[#E5E7EB] bg-white p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">Newsletter</p>
              <p className="font-semibold mt-1">Export subject, HTML preview, and plain text for email tools.</p>
            </div>
          </div>

          {loading ? (
            <div className="p-6 grid gap-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 bg-[#F5F5F5] animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="p-5 sm:p-6 space-y-5">
              {connectedAccounts.length === 0 && (
                <div className="border border-dashed border-[#E5E7EB] bg-[#F5F5F5] p-6">
                  <p className="font-display text-xl font-bold">No publishing accounts connected yet.</p>
                  <p className="text-sm text-[#525252] mt-2">
                    Connect LinkedIn profiles, LinkedIn Pages and WordPress to publish selected approved posts one by one.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <PublishingAccountColumn
                  title="LinkedIn Profiles"
                  platform="linkedin"
                  accounts={linkedinProfileAccounts}
                  slotLimit={publishingSlotLimit}
                  connecting={connectingId === "linkedin"}
                  workingId={workingId}
                  onConnect={connectLinkedIn}
                  onDisconnect={openDisconnectModal}
                />
                <PublishingAccountColumn
                  title="LinkedIn Pages"
                  platform="linkedin_pages"
                  accounts={linkedinPageAccounts}
                  slotLimit={linkedinPageSlotLimit}
                  unlimited={adminUser}
                  connecting={connectingId === "linkedin_pages"}
                  workingId={workingId}
                  onConnect={connectLinkedInPages}
                  onDisconnect={openDisconnectModal}
                />
                <PublishingAccountColumn
                  title="WordPress"
                  platform="wordpress"
                  accounts={wordpressAccounts}
                  slotLimit={publishingSlotLimit}
                  connecting={connectingId === "wordpress"}
                  workingId={workingId}
                  onConnect={() => setWordpressModalOpen(true)}
                  onDisconnect={openDisconnectModal}
                />
              </div>

              {currentPlan === "pro" && (
                <div className="border border-[#E5E7EB] bg-[#F5F5F5] p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Pro account capacity add-ons</p>
                      <p className="text-sm text-[#525252] mt-1">
                        Base capacity is 3 LinkedIn profiles/pages + 3 WordPress accounts. Add-ons are shown only for Pro accounts.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full md:w-auto">
                      {ACCOUNT_SLOT_ADDONS.map((addon) => (
                        <button
                          key={addon.key}
                          type="button"
                          disabled
                          className="border border-[#E5E7EB] bg-white px-3 py-2 text-left opacity-70 cursor-not-allowed"
                          title="Account add-on checkout is not enabled yet."
                        >
                          <span className="block text-xs font-bold">{addon.label}</span>
                          <span className="block text-[11px] text-[#737373]">{addon.price}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="container-wide pb-8 md:pb-10">
        <div className="border border-[#E5E7EB] bg-white">
          <div className="p-5 sm:p-6 border-b border-[#E5E7EB] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">Workflows</p>
              <h2 className="font-display text-2xl font-black mt-1">Recurring automation</h2>
            </div>

            <div className="flex flex-wrap gap-2">
              {WORKFLOW_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setWorkflowTab(tab.key)}
                  className={`px-3 py-2 text-xs font-semibold border ${
                    workflowTab === tab.key
                      ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                      : "border-[#E5E7EB] text-[#525252] hover:border-[#0A0A0A]"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="p-6 grid gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-[#F5F5F5] animate-pulse" />
              ))}
            </div>
          ) : filteredWorkflows.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title="No workflows here."
                text="Create a workflow to generate drafts on a schedule and send them into approvals."
              />
            </div>
          ) : (
            <div className="divide-y divide-[#E5E7EB]">
              {filteredWorkflows.map((workflow) => {
                const id = workflowId(workflow);
                const toolKey = workflowToolKey(workflow);
                const tool = getToolByKey(toolKey);
                const Icon = tool?.icon;
                const status = workflowStatus(workflow);
                const project = projectForItem(workflow);
                const archivedProject = projectArchivedForItem(workflow);

                return (
                <div key={id || workflowName(workflow)} className="p-5 sm:p-6" data-testid={`workflow-${id}`}>
                  <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4 min-w-0 w-full">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <StatusPill status={status} />
                        <StatusPill status={toolKey} />
                        {project && !isGenericProjectLabel(projectDisplayName(project, "")) && (
                            <StatusPill status={archivedProject ? `${projectDisplayName(project, "")} archived` : projectDisplayName(project, "")} />
                          )}
                      </div>

                      <div className="flex items-center gap-3">
                        {Icon && (
                          <div className="h-9 w-9 bg-[#0A0A0A] text-white flex items-center justify-center shrink-0">
                            <Icon size={16} weight="duotone" />
                          </div>
                        )}
                        <h3 className="font-display text-xl font-bold tracking-tight">
                          {cleanDisplayTitle(workflowName(workflow), defaultWorkflowNameForTool(toolKey))}
                        </h3>
                      </div>

                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#737373] font-mono">
                        <span>{workflowScheduleText(workflow)}</span>
                        {(workflow.next_run_at || workflow.next_run) && (
                          <span>Next: {formatDate(workflow.next_run_at || workflow.next_run)}</span>
                        )}
                        {(workflow.last_run_at || workflow.last_ran_at) && (
                          <span>Last: {formatDate(workflow.last_run_at || workflow.last_ran_at)}</span>
                        )}
                      </div>

                      {archivedProject && (
                        <p className="mt-3 border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-xs text-[#B91C1C]">
                          This project is archived. Workflow history is visible, but running, editing and publishing are disabled until the project is restored.
                        </p>
                      )}

                      {workflow.description && (
                        <p className="text-sm text-[#525252] mt-3 line-clamp-2">{workflow.description}</p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 shrink-0 max-w-full">
                      <button onClick={() => openEditModal(workflow)} className="btn-secondary !py-2 !px-3 text-xs">
                        <PencilSimple size={14} weight="bold" /> View / edit
                      </button>

                      <button onClick={() => openRunsModal(workflow)} className="btn-secondary !py-2 !px-3 text-xs">
                        <Eye size={14} weight="bold" /> Runs
                      </button>

                      <button
                        onClick={() => runWorkflowNow(workflow)}
                        disabled={archivedProject || String(workingId || "").startsWith("run-workflow:")}
                        className="btn-primary !py-2 !px-3 text-xs disabled:opacity-50"
                      >
                        <PlayCircle size={14} weight="bold" /> {workingId === `run-workflow:${id}` ? "Running..." : String(workingId || "").startsWith("run-workflow:") ? "Busy..." : "Run now"}
                      </button>

                      <button
                        onClick={() => toggleWorkflow(workflow)}
                        disabled={archivedProject || workingId === `toggle-workflow:${id}`}
                        className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5] inline-flex items-center gap-1.5 disabled:opacity-50"
                      >
                        {status === "active" ? (
                          <>
                            <Pause size={14} weight="bold" /> Pause
                          </>
                        ) : (
                          <>
                            <PlayCircle size={14} weight="bold" /> Resume
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="container-wide pb-8 md:pb-10">
        <div className="border border-[#E5E7EB] bg-white">
          <div className="p-5 sm:p-6 border-b border-[#E5E7EB] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">Approvals</p>
              <h2 className="font-display text-2xl font-black mt-1">Drafts waiting for review</h2>
            </div>

            <div className="flex flex-wrap gap-2">
              {APPROVAL_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setApprovalTab(tab.key)}
                  className={`px-3 py-2 text-xs font-semibold border ${
                    approvalTab === tab.key
                      ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                      : "border-[#E5E7EB] text-[#525252] hover:border-[#0A0A0A]"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="p-6 grid gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-[#F5F5F5] animate-pulse" />
              ))}
            </div>
          ) : filteredApprovals.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title="No approvals here."
                text="When background workflows generate drafts, they will appear here for review."
                to="/dashboard"
              />
            </div>
          ) : (
            <div className="divide-y divide-[#E5E7EB]">
              {filteredApprovals.map((item) => {
                const approvalId = item.approval_id || item.id;
                const liveUrl = publishedUrlForItem(item);
                const project = projectForItem(item);
                const archivedProject = projectArchivedForItem(item);

                return (
                  <div key={approvalId} className="p-5 sm:p-6" data-testid={`approval-${approvalId}`}>
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="min-w-0 max-w-full">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <StatusPill status={item.status} />
                          <StatusPill status={item.tool_key || item.tool} />
                          {project && !isGenericProjectLabel(projectDisplayName(project, "")) && (
                            <StatusPill status={archivedProject ? `${projectDisplayName(project, "")} archived` : projectDisplayName(project, "")} />
                          )}
                        </div>

                        <h3 className="font-display text-xl font-bold tracking-tight">{cleanDisplayTitle(item.title, "Draft")}</h3>

                        <p className="text-xs text-[#737373] font-mono mt-1">
                          Created {formatDate(item.created_at)}
                        </p>

                        {archivedProject && (
                          <p className="mt-3 border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-xs text-[#B91C1C]">
                            This project is archived. You can view this draft, but approval actions are disabled until the project is restored.
                          </p>
                        )}

                        <p className="text-sm text-[#525252] mt-3 line-clamp-2 whitespace-pre-line">
                          {item.content || item.output}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2 shrink-0 max-w-full">
                        <button onClick={() => setPreview(item)} className="btn-secondary !py-2 !px-3 text-xs">
                          <Eye size={14} weight="bold" /> View
                        </button>

                        {liveUrl && (
                          <button
                            type="button"
                            onClick={() => window.open(liveUrl, "_blank", "noopener,noreferrer")}
                            className="bg-[#002FA7] text-white px-3 py-2 text-xs font-bold hover:bg-[#002080] inline-flex items-center gap-1.5"
                          >
                            <Eye size={14} weight="bold" /> {publishedLinkLabel(item)}
                          </button>
                        )}

                        {(item.content || item.output) && (
                          <button
                            onClick={() => copyText(item.content || item.output, "Copied draft.")}
                            className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5] inline-flex items-center gap-1.5"
                          >
                            <Copy size={14} weight="bold" /> Copy
                          </button>
                        )}

                        {normalizeStatus(item.status) === "pending" && (
                          <>
                            <button
                              onClick={() => approve(item)}
                              disabled={archivedProject || workingId === `approve:${approvalId}`}
                              className="btn-primary !py-2 !px-3 text-xs"
                            >
                              <CheckCircle size={14} weight="bold" /> Approve
                            </button>

                            <button
                              onClick={() => openRejectModal(item)}
                              disabled={archivedProject || workingId === `reject:${approvalId}`}
                              className="border border-[#DC2626] text-[#DC2626] px-3 py-2 text-xs font-bold hover:bg-[#FEF2F2] inline-flex items-center gap-1.5"
                            >
                              <XCircle size={14} weight="bold" /> Reject
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="container-wide pb-12 md:pb-16">
        <div className="border border-[#E5E7EB] bg-white">
          <div className="p-5 sm:p-6 border-b border-[#E5E7EB] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">Action Board</p>
              <h2 className="font-display text-2xl font-black mt-1">Marketing tasks</h2>
              <p className="mt-2 text-xs text-[#737373] max-w-xl">
                Publish approved work directly to LinkedIn, create a WordPress draft, export a newsletter, or mark the task as published inside Ordeneli.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {TASK_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setTaskTab(tab.key)}
                  className={`px-3 py-2 text-xs font-semibold border ${
                    taskTab === tab.key
                      ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                      : "border-[#E5E7EB] text-[#525252] hover:border-[#0A0A0A]"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="p-6 grid gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-[#F5F5F5] animate-pulse" />
              ))}
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title="No tasks in this status."
                text="Approve a draft to create a ready-to-action marketing task."
              />
            </div>
          ) : (
            <div className="divide-y divide-[#E5E7EB]">
              {filteredTasks.map((item) => {
                const taskId = item.task_id || item.id;
                const status = normalizeStatus(item.status);
                const project = projectForItem(item);
                const archivedProject = projectArchivedForItem(item);
                const isSocialTask = isSocialActionTask(item);
                const taskActionDisabled = archivedProject;
                const canSchedule = isSocialTask && ["ready", "scheduled"].includes(status);
                const canPublish = isSocialTask && ["ready", "scheduled"].includes(status);
                const canComplete = !["failed", "archived", "completed"].includes(status);
                const liveUrl = publishedUrlForItem(item);
                const publishPlatform = publishPlatformForItem(item);
                const metadata = safeJsonObject(item.metadata);
                const scheduleMeta = safeJsonObject(metadata.schedule);
                const scheduledAccountId = String(item.account_id || scheduleMeta.account_id || metadata.account_id || "").trim();
                const scheduledAccount = connectedAccounts.find((account) => accountId(account) === scheduledAccountId) || null;
                const destination = destinationForScheduledTask(item, publishPlatform);
                const destinationLabel = publishingDestinationLabel(destination || publishPlatform, scheduledAccount);
                const selectedPostNumber = item.selected_post_number || metadata?.selected_post_number || scheduleMeta?.selected_post_number || null;
                const displayContent = visibleTaskBody(item);
                const showLinkedInAction = canPublish && primaryLinkedInAccount && !(status === "scheduled" && destination === "wordpress");
                const showWordPressAction = canPublish && primaryWordPressAccount && !(status === "scheduled" && (destination === "linkedin" || destination === "linkedin_pages"));
                const showNewsletterAction = canPublish && status !== "scheduled";
                const showPlanActions = !isSocialTask;

                return (
                  <div
                    key={taskId}
                    className={`p-5 sm:p-6 ${
                      item._isNestedActionTask
                        ? "ml-4 sm:ml-8 border-l-4 border-[#BFDBFE] bg-[#F8FBFF]"
                        : ""
                    }`}
                    data-testid={`action-task-${taskId}`}
                  >
                    <div
                      className={
                        showPlanActions
                          ? "flex flex-col gap-4 min-w-0 w-full"
                          : "grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5 min-w-0 w-full"
                      }
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <StatusPill status={item.status} />
                          <StatusPill status={item.task_type} />
                          {project && !isGenericProjectLabel(projectDisplayName(project, "")) && (
                            <StatusPill status={archivedProject ? `${projectDisplayName(project, "")} archived` : projectDisplayName(project, "")} />
                          )}
                        </div>

                        <h3 className="font-display text-xl font-bold tracking-tight break-words">{cleanDisplayTitle(item.title, "Task")}</h3>

                        {item._isNestedActionTask && (
                          <p className="mt-1 text-xs font-mono uppercase tracking-[0.16em] text-[#1D4ED8] break-words">
                            Generated from: {cleanDisplayTitle(item._parentTitle, "source task")}
                          </p>
                        )}

                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#737373] font-mono">
                          <span>Tool: {item.tool_key || item.tool}</span>
                          {isSocialTask && (destination || publishPlatform) && <span>Destination: {destinationLabel}</span>}
                          {isSocialTask && scheduledAccount && <span>Account: {accountLabel(scheduledAccount)}</span>}
                          {isSocialTask && selectedPostNumber && <span>Post: {selectedPostNumber}</span>}
                          {isSocialTask && item.scheduled_for && <span>Scheduled: {formatDate(item.scheduled_for)}</span>}
                          {item.published_at && <span>Published: {formatDate(item.published_at)}</span>}
                        </div>

                        {archivedProject && (
                          <p className="mt-3 border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-xs text-[#B91C1C]">
                            This task belongs to an archived project. You can view and copy it, but generating, scheduling, publishing and completion actions are disabled until the project is restored.
                          </p>
                        )}

                        {isSocialTask && status === "scheduled" && (
                          <p className="mt-3 border border-[#BFDBFE] bg-[#EFF6FF] px-3 py-2 text-xs text-[#1E40AF]">
                            Scheduled posts are checked automatically by the backend runner roughly every 10 minutes. A post scheduled for 10:00 may publish on the next runner check, usually by around 10:10–10:15 depending on queue and server timing.
                          </p>
                        )}

                        <p className="text-sm text-[#525252] mt-3 line-clamp-2 whitespace-pre-line">
                          {displayContent}
                        </p>
                      </div>

                      <div
                        className={
                          showPlanActions
                            ? "flex flex-wrap gap-2 w-full max-w-full min-w-0 overflow-hidden"
                            : "border-t border-[#E5E7EB] pt-4 xl:border-t-0 xl:border-l xl:pl-5 flex flex-wrap gap-2 content-start self-start w-full max-w-full min-w-0"
                        }
                      >
                        <button onClick={() => setPreview(item)} className="btn-secondary !py-2 !px-3 text-xs">
                          <Eye size={14} weight="bold" /> {isSocialTask ? "View" : "View plan"}
                        </button>

                        {liveUrl && (
                          <button
                            type="button"
                            onClick={() => window.open(liveUrl, "_blank", "noopener,noreferrer")}
                            className="bg-[#002FA7] text-white px-3 py-2 text-xs font-bold hover:bg-[#002080] inline-flex items-center gap-1.5"
                          >
                            <Eye size={14} weight="bold" /> {publishedLinkLabel(item)}
                          </button>
                        )}

                        {(item.content || item.output) && (
                          <button
                            onClick={() => copyText(displayContent, isSocialTask ? "Copied task." : "Copied plan.")}
                            className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5] inline-flex items-center gap-1.5"
                          >
                            <Copy size={14} weight="bold" /> {isSocialTask ? "Copy" : "Copy plan"}
                          </button>
                        )}

                        {showLinkedInAction && (
                          <button
                            onClick={() => setPublishingPreview({ type: "linkedin", item })}
                            disabled={archivedProject || workingId === `linkedin:${taskId}`}
                            className="border border-[#0A66C2] text-[#0A66C2] px-3 py-2 text-xs font-bold hover:bg-[#EFF6FF] inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            <PaperPlaneTilt size={14} weight="bold" /> {workingId === `linkedin:${taskId}` ? "Publishing..." : "LinkedIn preview"}
                          </button>
                        )}

                        {showWordPressAction && (
                          <button
                            onClick={() => setPublishingPreview({ type: "wordpress", item })}
                            disabled={archivedProject || workingId === `wordpress:${taskId}`}
                            className="border border-[#002FA7] text-[#002FA7] px-3 py-2 text-xs font-bold hover:bg-[#EFF6FF] inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            <Plus size={14} weight="bold" /> {workingId === `wordpress:${taskId}` ? "Creating..." : "WordPress preview"}
                          </button>
                        )}

                        {showNewsletterAction && (
                          <button
                            onClick={() => exportNewsletter(item)}
                            disabled={archivedProject || workingId === `newsletter:${taskId}`}
                            className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5] inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            <Copy size={14} weight="bold" /> {workingId === `newsletter:${taskId}` ? "Exporting..." : "Newsletter"}
                          </button>
                        )}

                        {showPlanActions && (
                          <>
                            <button
                              onClick={() => downloadPlan(item)}
                              className={PLAN_NEUTRAL_BUTTON_CLASS}
                            >
                              <Copy size={14} weight="bold" /> Download plan
                            </button>

                            <button
                              onClick={() => runPlanAction(item, "create-checklist", "checklist", "Checklist generation started.")}
                              disabled={archivedProject || workingId === `checklist:${taskId}`}
                              className={PLAN_NEUTRAL_BUTTON_CLASS}
                            >
                              <CheckCircle size={14} weight="bold" />{" "}
                              {workingId === `checklist:${taskId}` ? "Creating..." : "Create tasks/checklist"}
                            </button>

                            <button
                              onClick={() => runPlanAction(item, "generate-gbp-posts", "gbp-posts", "GBP post generation started.")}
                              disabled={archivedProject || workingId === `gbp-posts:${taskId}`}
                              className={PLAN_GENERATOR_BUTTON_CLASS}
                            >
                              <Plus size={14} weight="bold" />{" "}
                              {workingId === `gbp-posts:${taskId}` ? "Generating..." : "Generate GBP posts from this plan"}
                            </button>

                            <button
                              onClick={() => runPlanAction(item, "generate-linkedin-post", "linkedin-post", "LinkedIn post generation started.")}
                              disabled={archivedProject || workingId === `linkedin-post:${taskId}`}
                              className={PLAN_GENERATOR_BUTTON_CLASS}
                            >
                              <Plus size={14} weight="bold" />{" "}
                              {workingId === `linkedin-post:${taskId}` ? "Generating..." : "Generate LinkedIn post from this plan"}
                            </button>

                            <button
                              onClick={() => runPlanAction(item, "generate-wordpress-post", "wordpress-post", "WordPress post generation started.")}
                              disabled={archivedProject || workingId === `wordpress-post:${taskId}`}
                              className={PLAN_GENERATOR_BUTTON_CLASS}
                            >
                              <Plus size={14} weight="bold" />{" "}
                              {workingId === `wordpress-post:${taskId}` ? "Generating..." : "Generate WordPress post from this plan"}
                            </button>

                            <button
                              onClick={() => runPlanAction(item, "generate-wordpress-landing-page", "wp-landing", "WordPress landing page generation started.")}
                              disabled={archivedProject || workingId === `wp-landing:${taskId}`}
                              className={PLAN_GENERATOR_BUTTON_CLASS}
                            >
                              <Plus size={14} weight="bold" />{" "}
                              {workingId === `wp-landing:${taskId}` ? "Generating..." : "Generate WordPress landing page from this plan"}
                            </button>

                            <button
                              onClick={() => runPlanAction(item, "generate-blog-post", "blog-post", "Blog post generation started.")}
                              disabled={archivedProject || workingId === `blog-post:${taskId}`}
                              className={PLAN_GENERATOR_BUTTON_CLASS}
                            >
                              <Plus size={14} weight="bold" />{" "}
                              {workingId === `blog-post:${taskId}` ? "Generating..." : "Generate blog post from this plan"}
                            </button>
                          </>
                        )}

                        {canSchedule && (
                          <button
                            onClick={() => openScheduleModal(item)}
                            disabled={taskActionDisabled}
                            className="btn-secondary !py-2 !px-3 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                            title={status === "scheduled" ? "Edit the scheduled date, destination or selected post" : hasMultiplePublishingParts(item, "linkedin") ? "Choose which post to schedule" : "Schedule task"}
                          >
                            <CalendarPlus size={14} weight="bold" /> {status === "scheduled" ? "Edit schedule" : hasMultiplePublishingParts(item, "linkedin") ? "Schedule one post" : "Schedule"}
                          </button>
                        )}

                        {canPublish && (
                          <button
                            onClick={() => publishTask(item)}
                            disabled={taskActionDisabled || workingId === `publish:${taskId}`}
                            className="btn-primary !py-2 !px-3 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <PaperPlaneTilt size={14} weight="bold" /> Mark published
                          </button>
                        )}

                        {canComplete && (
                          <button
                            onClick={() => completeTask(item)}
                            disabled={taskActionDisabled || workingId === `complete:${taskId}`}
                            className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5] inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <PlayCircle size={14} weight="bold" /> Complete
                          </button>
                        )}

                        {status !== "archived" && (
                          <button
                            onClick={() => openArchiveModal(item)}
                            disabled={taskActionDisabled}
                            className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5] inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Archive size={14} weight="bold" /> Archive
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <WorkflowCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={createWorkflow}
        loading={creatingWorkflow}
        mode="create"
        projects={projects}
        viewOnly={false}
      />

      <WorkflowCreateModal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, workflow: null })}
        onSubmit={updateWorkflow}
        loading={savingWorkflow}
        initialWorkflow={editModal.workflow}
        mode="edit"
        projects={projects}
        viewOnly={projectArchivedForItem(editModal.workflow)}
      />

      <WorkflowRunsModal
        open={runsModal.open}
        onClose={() => setRunsModal({ open: false, workflow: null, runs: [], loading: false })}
        workflow={runsModal.workflow}
        runs={runsModal.runs}
        loading={runsModal.loading}
      />

      <PreviewModal
        item={preview}
        onClose={() => setPreview(null)}
        onSaveAsActionTask={saveGeneratedPreviewAsActionTask}
        savingActionTask={Boolean(workingId && workingId.startsWith("save-action-task:"))}
      />

      <LinkedInPagePickerModal
        state={linkedinPagePicker}
        onClose={() => setLinkedinPagePicker((prev) => ({ ...prev, open: false }))}
        onToggle={toggleLinkedInPageSelection}
        onSave={saveLinkedInPageSelection}
        onRefresh={openLinkedInPagePicker}
      />

      <WordPressConnectModal
        open={wordpressModalOpen}
        onClose={() => setWordpressModalOpen(false)}
        onSubmit={connectWordPress}
        loading={connectingId === "wordpress"}
      />

      <PublishingPreviewModal
        preview={publishingPreview}
        accounts={publishingPreview?.type === "linkedin" ? linkedinAccounts : wordpressAccounts}
        selectedAccountId={publishingPreview?.type === "linkedin" ? accountId(selectedLinkedInAccount) : accountId(selectedWordPressAccount)}
        onAccountChange={setPublishingAccountForType}
        loading={Boolean(workingId && (workingId.startsWith("linkedin:") || workingId.startsWith("wordpress:")))}
        onClose={() => setPublishingPreview(null)}
        onConfirm={confirmPublishingPreview}
        mediaUploading={Boolean(workingId && workingId === `media-upload:${actionTaskId(publishingPreview?.item)}`)}
        mediaRemoving={Boolean(workingId && workingId === `media-remove:${actionTaskId(publishingPreview?.item)}`)}
        onUploadMedia={uploadTaskMedia}
        onRemoveMedia={removeTaskMedia}
        mediaDisabled={projectArchivedForItem(publishingPreview?.item)}
      />

      <NewsletterExportModal
        exportData={newsletterExport}
        onClose={() => setNewsletterExport(null)}
      />

      <ConfirmActionModal
        open={disconnectModal.open}
        title="Disconnect account?"
        subtitle="Connected publishing"
        message={`Disconnect ${accountLabel(disconnectModal.account)}?

You can reconnect this account later, or connect a different LinkedIn/WordPress account in its slot.`}
        confirmLabel="Disconnect"
        danger
        loading={workingId === `disconnect:${accountId(disconnectModal.account)}`}
        onClose={() => setDisconnectModal({ open: false, account: null })}
        onConfirm={disconnectPublishingAccount}
      />

      <ModalShell
        open={rejectModal.open}
        onClose={() => setRejectModal({ open: false, approvalId: null, title: "", feedback: "" })}
        title="Reject draft"
        subtitle="Approval action"
        footer={
          <>
            <button
              onClick={() => setRejectModal({ open: false, approvalId: null, title: "", feedback: "" })}
              className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5]"
            >
              Cancel
            </button>

            <button
              onClick={submitReject}
              disabled={workingId === `reject:${rejectModal.approvalId}`}
              className="bg-[#DC2626] text-white px-4 py-2 text-sm font-semibold hover:bg-[#B91C1C]"
            >
              {workingId === `reject:${rejectModal.approvalId}` ? "Rejecting..." : "Reject draft"}
            </button>
          </>
        }
      >
        <p className="text-sm text-[#525252] mb-3">
          Add optional feedback for <span className="font-semibold text-[#0A0A0A]">{cleanDisplayTitle(rejectModal.title, "this draft")}</span>.
        </p>

        <textarea
          value={rejectModal.feedback}
          onChange={(e) => setRejectModal((prev) => ({ ...prev, feedback: e.target.value }))}
          rows={5}
          placeholder="Optional reason or feedback..."
          className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#002FA7]"
        />
      </ModalShell>

      <ModalShell
        open={scheduleModal.open}
        onClose={closeScheduleModal}
        title="Schedule task"
        subtitle="Task action"
        footer={
          <>
            <button
              onClick={closeScheduleModal}
              className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5]"
            >
              Cancel
            </button>

            <button
              onClick={submitSchedule}
              disabled={workingId === `schedule:${scheduleModal.taskId}`}
              className="bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#002080]"
            >
              {workingId === `schedule:${scheduleModal.taskId}` ? "Saving..." : `Save ${scheduleModal.destination === "wordpress" ? "WordPress" : "LinkedIn"} schedule`}
            </button>
          </>
        }
      >
        <p className="text-sm text-[#525252] mb-3">
          Choose a date and time for <span className="font-semibold text-[#0A0A0A]">{cleanDisplayTitle(scheduleModal.title, "this task")}</span>.
        </p>

        {(() => {
          const parts = publishingPartsForItem(scheduleModal.task, scheduleModal.destination || "linkedin");
          if (parts.length <= 1) return null;

          return (
            <div className="mb-4 border border-[#BFDBFE] bg-[#EFF6FF] p-3">
              <p className="text-sm font-semibold text-[#1E40AF]">
                This task contains {parts.length} separate posts. Choose which one to schedule.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {parts.map((part, index) => (
                  <button
                    key={part.id || index}
                    type="button"
                    onClick={() => setScheduleModal((prev) => ({ ...prev, selectedPartIndex: index }))}
                    className={`px-3 py-2 text-xs font-bold border ${
                      scheduleModal.selectedPartIndex === index
                        ? "bg-[#002FA7] border-[#002FA7] text-white"
                        : "bg-white border-[#BFDBFE] text-[#1E40AF] hover:border-[#002FA7]"
                    }`}
                  >
                    {part.title}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#1E40AF] mt-3">
                Ordeneli will schedule the selected post details only, not the whole campaign output.
              </p>
            </div>
          );
        })()}

        {(() => {
          const destination = scheduleModal.destination || "linkedin";
          const parts = publishingPartsForItem(scheduleModal.task, destination);
          const selectedPart = parts[scheduleModal.selectedPartIndex] || parts[0] || null;
          const selectedText = appendCtaUrlForPreview(selectedPart?.text || "", scheduleModal.ctaUrl);

          if (!selectedPart) return null;

          return (
            <div className="mb-4 border border-[#E5E7EB] bg-[#FAFAFA] p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">
                  Selected {destination === "wordpress" ? "WordPress draft" : "LinkedIn post"}
                </p>
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#002FA7] bg-[#EFF6FF] border border-[#BFDBFE] px-2 py-1">
                  {selectedPart.title}
                </span>
              </div>
              <div className="text-sm text-[#0A0A0A] whitespace-pre-line line-clamp-6">
                {selectedText}
              </div>
            </div>
          );
        })()}

        <TaskMediaPanel
          item={scheduleModal.task}
          disabled={projectArchivedForItem(scheduleModal.task)}
          uploading={workingId === `media-upload:${scheduleModal.taskId}`}
          removing={workingId === `media-remove:${scheduleModal.taskId}`}
          onUpload={uploadTaskMedia}
          onRemove={removeTaskMedia}
          context="schedule"
        />

        <div className="mb-4 border border-[#E5E7EB] bg-white p-3">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#737373] mb-3">
            Schedule destination
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() =>
                setScheduleModal((prev) => ({
                  ...prev,
                  destination: "linkedin",
                  accountId: accountId(selectedLinkedInAccount || linkedinAccounts[0]) || "",
                }))
              }
              disabled={linkedinAccounts.length < 1}
              className={`border px-3 py-2 text-sm font-bold text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                scheduleModal.destination === "linkedin"
                  ? "bg-[#002FA7] border-[#002FA7] text-white"
                  : "bg-white border-[#E5E7EB] text-[#111] hover:border-[#002FA7]"
              }`}
            >
              LinkedIn schedule
              <span className="block text-xs font-normal mt-1 opacity-80">
                Schedule the selected post to a connected LinkedIn profile.
              </span>
            </button>

            <button
              type="button"
              onClick={() =>
                setScheduleModal((prev) => ({
                  ...prev,
                  destination: "wordpress",
                  accountId: accountId(selectedWordPressAccount || wordpressAccounts[0]) || "",
                }))
              }
              disabled={wordpressAccounts.length < 1}
              className={`border px-3 py-2 text-sm font-bold text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                scheduleModal.destination === "wordpress"
                  ? "bg-[#002FA7] border-[#002FA7] text-white"
                  : "bg-white border-[#E5E7EB] text-[#111] hover:border-[#002FA7]"
              }`}
            >
              WordPress schedule
              <span className="block text-xs font-normal mt-1 opacity-80">
                Schedule the selected draft for a connected WordPress site.
              </span>
            </button>
          </div>
        </div>

        {(() => {
          const destination = scheduleModal.destination || "linkedin";
          const accounts = destination === "wordpress" ? wordpressAccounts : linkedinAccounts;
          const label = destination === "wordpress" ? "WordPress site" : "LinkedIn account";

          return (
            <div className="mb-4">
              <label className="block text-xs font-mono uppercase tracking-[0.18em] text-[#737373] mb-2">
                {label}
              </label>

              <select
                value={scheduleModal.accountId || accountId(accounts[0]) || ""}
                onChange={(e) => setScheduleModal((prev) => ({ ...prev, accountId: e.target.value }))}
                className="w-full border border-[#E5E7EB] bg-white px-3 py-2 text-sm outline-none focus:border-[#002FA7]"
              >
                {accounts.length < 1 ? (
                  <option value="">No connected {label.toLowerCase()}</option>
                ) : (
                  accounts.map((entry) => (
                    <option key={accountId(entry)} value={accountId(entry)}>
                      {accountLabel(entry)}
                    </option>
                  ))
                )}
              </select>
            </div>
          );
        })()}

        <label className="mb-4 flex flex-col gap-1.5">
          <span className="text-xs font-mono uppercase tracking-[0.18em] text-[#737373]">Optional link / CTA URL</span>
          <input
            value={scheduleModal.ctaUrl || ""}
            onChange={(e) => setScheduleModal((prev) => ({ ...prev, ctaUrl: e.target.value }))}
            placeholder="https://yourbusiness.com"
            className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#002FA7]"
          />
          <span className="text-xs text-[#737373]">Optional. Add a customer website, offer page, booking page, or blog link.</span>
        </label>

        <input
          type="datetime-local"
          value={scheduleModal.scheduledFor}
          onChange={(e) => setScheduleModal((prev) => ({ ...prev, scheduledFor: e.target.value }))}
          className="w-full border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#002FA7]"
        />

        <p className="text-xs text-[#737373] mt-2">
          This is entered in your local time and sent to the backend as UTC.
        </p>
      </ModalShell>

      <ModalShell
        open={archiveModal.open}
        onClose={() => setArchiveModal({ open: false, taskId: null, title: "" })}
        title="Archive task"
        subtitle="Task action"
        footer={
          <>
            <button
              onClick={() => setArchiveModal({ open: false, taskId: null, title: "" })}
              className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5]"
            >
              Cancel
            </button>

            <button
              onClick={submitArchive}
              disabled={workingId === `archive:${archiveModal.taskId}`}
              className="bg-[#0A0A0A] text-white px-4 py-2 text-sm font-semibold hover:bg-[#262626]"
            >
              {workingId === `archive:${archiveModal.taskId}` ? "Archiving..." : "Archive"}
            </button>
          </>
        }
      >
        <p className="text-sm text-[#525252]">
          Are you sure you want to archive <span className="font-semibold text-[#0A0A0A]">{cleanDisplayTitle(archiveModal.title, "this task")}</span>?
        </p>
      </ModalShell>
    </div>
  );
}
